import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { colors, API, fmt, authHeaders, getTitularId, temOperacao } from '../utils/theme';
import {
  PageHeader, StatCard, Badge, Button, LoadingSpinner, SearchableSelect,
} from '../components/UI';
import DetalheXmilesModal from '../components/DetalheXmilesModal';
import CentrosFechamentoModal from '../components/CentrosFechamentoModal';
import { ChevronLeft, ChevronRight, Link2, Unlink, Check, X, Zap, ArrowRight, UserX, Undo2, PlusCircle, FileText, Eye, ArrowDownCircle, Filter, Pencil, History, CheckCircle2, CheckSquare, Square, Repeat, Building2, AlertTriangle, Upload, RefreshCw } from 'lucide-react';

const mesAtual = () => new Date().toISOString().slice(0, 7);

const ORIGEM_LABEL = { FATURA_PDF: 'PDF', OPEN_FINANCE: 'Open Finance' };

// Empresas para vincular lançamentos/conciliações (chamado #31). Vazio = sem empresa.
// TK Milhas/XMTech24/Radar Voo adicionadas em 02/09/2026 — o server valida a
// mesma lista (EMPRESAS_VALIDAS em routes/conciliacao.js); mudou lá, muda aqui.
const EMPRESAS = ['Ivoei', 'XMiles', 'Franchising', 'TK Milhas', 'XMTech24', 'Radar Voo'];

// ── Operação (centro de fechamento) direto no painel — 02/09/2026 ────────────
// O que se marca aqui grava em transacoes.centro_fechamento da linha da FATURA,
// o mesmo campo que o relatório Consulta → Fechamento lê com COALESCE(manual,
// regra): a marca manual ganha da regra automática; limpar devolve pra regra.
// PESSOAL simples/EXCLUIDO ficam fora do seletor — têm fluxo próprio (botão
// Pessoal da Rayssa / Pgto). Pessoal X/J são outra coisa (02/09/2026): gasto
// pessoal ATRIBUÍDO a uma operação, marca manual só de centro — não viram
// tipo_vinculo PESSOAL nem entram nos totais de "gasto pessoal".
const OPERACOES_SISTEMA = [
  { v: 'OPERACAO_X', label: 'Operação X' },
  { v: 'OPERACAO_J', label: 'Operação J' },
  // De sistema desde 07/10/2026 — a regra aponta pra eles.
  { v: 'OPERACAO_AGENCIA', label: 'Operação Agência' },
  { v: 'TECNOLOGIA_XMTECH24', label: 'Tecnologia · XMTech24' },
  { v: 'COMPRAS_X', label: 'Compras X' },
  { v: 'COMPRAS_J', label: 'Compras J' },
  { v: 'TAXA_EMBARQUE_X', label: 'Taxa embarque X' },
  { v: 'TAXA_EMBARQUE_J', label: 'Taxa embarque J' },
  { v: 'PESSOAL_X', label: 'Pessoal X' },
  { v: 'PESSOAL_J', label: 'Pessoal J' },
];
// Centros CADASTRADOS pelo gestor (06/10/2026 — ex.: Operação Agência) entram
// depois dos de sistema. Carregados uma vez pela página; ficam no módulo
// porque OPERACAO_LABEL e as listas são lidas por vários pontos deste arquivo.
// O rótulo guarda também os desativados: linha antiga continua com o nome.
let OPERACOES = OPERACOES_SISTEMA;
let ROTULO_CADASTRADO = {};
function registrarCentrosCadastrados(lista) {
  const cad = (lista || []).filter(c => !c.sistema);
  ROTULO_CADASTRADO = Object.fromEntries(cad.map(c => [c.codigo, c.rotulo]));
  OPERACOES = [...OPERACOES_SISTEMA, ...cad.filter(c => c.ativo).map(c => ({ v: c.codigo, label: c.rotulo }))];
}
const OPERACAO_LABEL = (v) => ({
  PESSOAL: 'Pessoal', EXCLUIDO: 'Excluído',
  ...Object.fromEntries(OPERACOES_SISTEMA.map(o => [o.v, o.label])),
  ...ROTULO_CADASTRADO,
}[v] || v);
const OPERACAO_COR = (v) => (v || '').endsWith('_X') ? '#f59e0b' : '#22c55e';

// A sugestão de operação do casamento NÃO é mais calculada aqui (06/10/2026).
// Havia uma cópia da regra do servidor (texto + data) que não conhecia o
// centro padrão do cartão nem o "fora da operação" (iVoei/Apollo/Marcus), e o
// que ela sugeria era gravado como ESCOLHA À MÃO no casamento — passando por
// cima da regra. Uma linha da Jessica (cartão Pessoal) conciliada virava
// Operação J. Agora o servidor manda `centro_regra` em cada linha da fatura, o
// casamento pré-seleciona "auto" (não grava nada) e mostra o que a regra dá.

// Vínculo de rastreabilidade (chamado #47) — rótulos e cores por tipo.
const VINCULO_LABEL = (t) => ({
  ESTOQUE: 'Estoque', EMISSAO: 'Emissão', PESSOAL: 'Pessoal', ENCARGO: 'Encargo', OUTRO: 'Outro',
}[t] || t);
const VINCULO_COR = (t) => ({
  ESTOQUE: '#8b5cf6', EMISSAO: '#0ea5e9', PESSOAL: '#f59e0b', ENCARGO: '#ef4444', OUTRO: '#64748b',
}[t] || '#64748b');

// Dia do vencimento a partir do que o cadastro tiver: ora só o dia ('15'), ora
// uma data ISO completa. Estava duplicado inline no dropdown e na filtragem das
// listas (chamados #1/#15) — duas cópias que precisavam concordar.
export const diaDoVencimento = (raw) => {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (/^\d{1,2}$/.test(s)) return parseInt(s, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? parseInt(m[3], 10) : null;
};

// Preview do parcelamento (só exibição — quem divide de verdade é o servidor,
// em lib/parcelamento-local.js; a resposta do POST é o que a tela passa a
// mostrar). Mesmas duas regras: a sobra dos centavos vai na 1ª parcela, e as
// seguintes vencem no dia da FATURA do mês delas.
export const previewParcelamento = ({ valorTotal, dataBase, quantidade, diaVenc }) => {
  const n = parseInt(quantidade, 10);
  const total = Math.round((parseFloat(valorTotal) || 0) * 100);
  if (!Number.isInteger(n) || n < 2 || total < n || !/^\d{4}-\d{2}-\d{2}$/.test(String(dataBase || ''))) return null;
  const base = Math.floor(total / n);
  const primeira = base + (total - base * n);
  const [ano, mes, dia] = String(dataBase).split('-').map(Number);
  // O mês de fatura da 1ª: passou do vencimento, cai no ciclo seguinte.
  const ultimoDia = (y, m) => new Date(y, m, 0).getDate();
  const iso = (y, m, d) => `${y}-${String(m).padStart(2, '0')}-${String(Math.min(d, ultimoDia(y, m))).padStart(2, '0')}`;
  let mesInicial = { ano, mes };
  if (diaVenc && dia > Math.min(diaVenc, ultimoDia(ano, mes))) {
    mesInicial = mes === 12 ? { ano: ano + 1, mes: 1 } : { ano, mes: mes + 1 };
  }
  return Array.from({ length: n }, (_, i) => {
    const bruto = mesInicial.mes - 1 + i;
    const y = mesInicial.ano + Math.floor(bruto / 12);
    const m = (bruto % 12) + 1;
    return {
      numero: i + 1,
      valor: (i === 0 ? primeira : base) / 100,
      data: i === 0 ? dataBase : iso(y, m, diaVenc || dia),
      mes: `${y}-${String(m).padStart(2, '0')}`,
    };
  });
};

// Categorias LOCAIS do Finance que o plano de contas não conhece (02/09/2026): o
// desconto de antecipação é classificado como Bonus/Antecipação por aqui (a
// regra de boot tagueia as linhas da fatura com exatamente estas strings).
const CATEGORIAS_LOCAIS = [
  { group: 'Bonus', name: 'Antecipação' },
];
// Categorias do lançamento (group = categoria, name = subcategoria).
// Compartilhado pelo modal de lançamento e pelo avulso em lote. Cada entrada
// carrega `local: true` quando foi criada no Finance (sistema=0) — essas são
// bloqueadas pra empresa XMiles, cuja categoria nasce no TechMiles (DRE) — e
// `xm` com o id do plano de contas quando a categoria VEM do plano.
//
// Fonte primária: o plano de contas do TechMiles, espelhado em categorias_custo
// (18/09/2026). Era o Manager, que caiu quando as credenciais saíram do .env —
// o 500 sumia no .catch e o dropdown despencou pra uma categoria só (Bonus).
// O Manager segue como FALLBACK, depois do plano: se um dia responder de novo,
// o dedupe abaixo descarta o que repetir e quem chegou primeiro (o plano) vence.
function useTiposDespesa() {
  const [plano, setPlano] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [locais, setLocais] = useState([]);
  useEffect(() => {
    let ok = true;
    fetch(`${API}/plano-contas/subcategorias`, { headers: authHeaders() }).then(r => r.json())
      .then(d => { if (ok && Array.isArray(d)) setPlano(d); })
      .catch(() => {});
    fetch(`${API}/xmiles/expense-types`).then(r => r.json())
      .then(d => { if (ok) setTipos(Array.isArray(d) ? d : (Array.isArray(d?.data) ? d.data : [])); })
      .catch(() => {});
    fetch(`${API}/conciliacao/categorias-finance`, { headers: authHeaders() }).then(r => r.json())
      .then(d => { if (ok && Array.isArray(d)) setLocais(d); })
      .catch(() => {});
    return () => { ok = false; };
  }, []);
  return useMemo(() => {
    const base = [
      ...plano.map(s => ({ group: s.grupo, name: s.nome, local: false, xm: s.xm_categoria_id })),
      ...tipos.map(t => ({ group: t.group, name: t.name, local: false, xm: null })),
      ...CATEGORIAS_LOCAIS.map(c => ({ ...c, local: false, xm: null })),
      ...locais.map(c => ({ group: c.grupo, name: c.subcategoria, local: !Number(c.sistema), xm: null })),
    ];
    const vistos = new Set();
    return base.filter(e => {
      const k = `${(e.group || '').toUpperCase()}|${(e.name || '').toUpperCase()}`;
      if (vistos.has(k)) return false;
      vistos.add(k);
      return true;
    });
  }, [plano, tipos, locais]);
}

// Selects de categoria/subcategoria com criação de categoria local (03/09/2026).
// Regra do gestor: categoria da XMiles nasce no TECHMILES (o DRE de lá é quem
// consome) — criar categoria aqui só vale pra outras empresas. Com XMiles
// selecionada, as locais somem do dropdown e o "+ Nova categoria" não aparece.
// O server valida a mesma regra (categoriaVetadaParaXMiles).
function CamposCategoria({ categoria, subcategoria, empresa, onChange, inp, sel, lbl }) {
  const tipos = useTiposDespesa();
  const [extras, setExtras] = useState([]); // criadas nesta sessão do modal
  const [criando, setCriando] = useState(false);
  const [novo, setNovo] = useState({ grupo: '', sub: '' });
  const [criandoBusy, setCriandoBusy] = useState(false);
  const bloqueiaLocais = empresa === 'XMiles';
  const todos = [...tipos, ...extras];
  const visiveis = bloqueiaLocais ? todos.filter(t => !t.local) : todos;
  const grupos = [...new Set(visiveis.map(e => e.group).filter(Boolean))].sort();
  // Carrega o `xm` junto: é ao escolher a SUBcategoria que o vínculo com o
  // plano de contas fica definido (grupo sozinho não classifica lançamento).
  const subs = visiveis.filter(e => e.group === categoria && e.name)
    .sort((a, b) => a.name.localeCompare(b.name));

  // Trocou a empresa pra XMiles com uma categoria local selecionada? Limpa —
  // o server recusaria de qualquer jeito.
  useEffect(() => {
    if (bloqueiaLocais && categoria && todos.length
        && !todos.some(t => t.group === categoria && !t.local)) {
      onChange({ categoria: '', subcategoria: '', xm_categoria_id: null });
    }
  }, [bloqueiaLocais]); // eslint-disable-line

  const criar = async () => {
    const grupo = novo.grupo.trim();
    if (!grupo || criandoBusy) return;
    setCriandoBusy(true);
    try {
      const r = await fetch(`${API}/conciliacao/categorias-finance`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ grupo, subcategoria: novo.sub.trim() || null }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      setExtras(x => [...x, { group: grupo, name: novo.sub.trim() || null, local: true }]);
      onChange({ categoria: grupo, subcategoria: novo.sub.trim() || '', xm_categoria_id: null });
      setCriando(false); setNovo({ grupo: '', sub: '' });
    } catch (e) { alert('Criar categoria: ' + e.message); }
    setCriandoBusy(false);
  };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={lbl}>Categoria (grupo de despesa)</label>
          <select style={sel} value={categoria}
            onChange={e => {
              if (e.target.value === '__nova__') { setCriando(true); return; }
              onChange({ categoria: e.target.value, subcategoria: '', xm_categoria_id: null });
            }}>
            <option value="">{grupos.length ? 'Selecione...' : 'Carregando...'}</option>
            {/* No TOPO de propósito: no fim de 30 categorias ninguém achava. */}
            {!bloqueiaLocais && <option value="__nova__">＋ Nova categoria…</option>}
            {grupos.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
        </div>
        <div>
          <label style={lbl}>Subcategoria (despesa)</label>
          <select style={sel} value={subcategoria} disabled={!categoria}
            onChange={e => {
              const alvo = subs.find(x => x.name === e.target.value);
              onChange({ subcategoria: e.target.value, xm_categoria_id: alvo?.xm ?? null });
            }}>
            <option value="">{!categoria ? 'Escolha a categoria primeiro' : 'Selecione...'}</option>
            {subs.map(s => <option key={s.name} value={s.name}>{s.name}</option>)}
          </select>
        </div>
      </div>
      {bloqueiaLocais && (
        <div style={{ fontSize: 10, color: colors.textDim, marginTop: 4 }}>
          Empresa XMiles: só categorias do TechMiles — o DRE nasce lá. Categoria nova aqui é só pra outras empresas.
        </div>
      )}
      {criando && !bloqueiaLocais && (
        <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input style={{ ...inp, width: 170 }} placeholder="Nova categoria" value={novo.grupo}
            onChange={e => setNovo(n => ({ ...n, grupo: e.target.value }))} />
          <input style={{ ...inp, width: 170 }} placeholder="Subcategoria (opcional)" value={novo.sub}
            onChange={e => setNovo(n => ({ ...n, sub: e.target.value }))} />
          <button onClick={criar} disabled={!novo.grupo.trim() || criandoBusy}
            style={{
              padding: '6px 12px', borderRadius: 8, border: 'none', background: colors.primary,
              color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600,
            }}>
            {criandoBusy ? 'Criando...' : 'Criar'}
          </button>
          <button onClick={() => { setCriando(false); setNovo({ grupo: '', sub: '' }); }}
            style={{
              padding: '6px 10px', borderRadius: 8, border: `1px solid ${colors.border}`,
              background: 'transparent', color: colors.textMuted, cursor: 'pointer', fontSize: 12,
            }}>Cancelar</button>
        </div>
      )}
    </div>
  );
}

const isPagamento = (desc) => {
  const d = (desc || '').toLowerCase();
  return d.includes('pagamento') || d.includes('pgto') || d.includes('pagto') ||
    d.includes('pag.') || d.includes('debito minimo') || d.includes('débito mínimo');
};

export const tituloTransacaoXmiles = (transacao) => {
  if (transacao?.origem !== 'XM_PRODUCAO') return transacao?.descricao;
  // Campo vazio SAI do título; não vira traço. Marcar a ausência com "-" gerava
  // "CARTAO - - - Carrinho LIVELO→SMILES" e a leitura sugeria dado perdido,
  // quando é dado que nunca existiu: dos lançamentos XM pendentes, 2.217 de
  // 5.026 não têm fidelizado e 230 não têm categoria.
  return [
    transacao.nome_cartao,
    transacao.fidelizado,
    transacao.categoria,
    transacao.descricao,
  ].map(valor => String(valor ?? '').trim()).filter(Boolean).join(' - ');
};

export default function ConciliacaoPage() {
  // Centros cadastrados: carrega e força re-render quando a lista muda.
  const [, setVersaoCentros] = useState(0);
  const [centrosOpen, setCentrosOpen] = useState(false);
  const carregarCentros = useCallback(() => {
    fetch(`${API}/centros-fechamento?todos=1`, { headers: authHeaders() })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (Array.isArray(d)) { registrarCentrosCadastrados(d); setVersaoCentros(v => v + 1); } })
      .catch(() => {});
  }, []);
  useEffect(() => { carregarCentros(); }, [carregarCentros]);
  // Investidor (Titular) só enxerga os cartões dele: trava o titular no dele e
  // esconde o seletor. Gestor → null (vê todos, seletor livre).
  // Com OPERAÇÃO o usuário escolhe entre os titulares dela; sem operação, o
  // seletor continua travado no titular dele. readOnly NÃO muda: ver o grupo
  // não vira permissão de editar.
  const titularProprio = getTitularId();
  const titularRestrito = temOperacao() ? null : titularProprio;
  // Investidor é READ-ONLY: vê os dados dele, sem botões de ação (nada de match,
  // lançamento, finalizar, desfazer, classificar). Gestor mantém tudo.
  const readOnly = !!titularProprio;
  const [mes, setMes] = useState(mesAtual());
  const [titulares, setTitulares] = useState([]);
  const [titularId, setTitularId] = useState(titularRestrito ? String(titularRestrito) : '');
  const [cartoes, setCartoes] = useState([]);
  const [cartaoId, setCartaoId] = useState('');
  const [busca, setBusca] = useState('');
  const [data, setData] = useState(null);
  // Chamado #43: conciliação finalizada trava a edição dos lançamentos.
  const finalizada = !!data?.finalizada;
  // Pode editar/mexer nos lançamentos? Não pra investidor (readOnly) nem quando
  // a conciliação do cartão/mês está finalizada.
  const editavel = !readOnly && !finalizada;
  // Chamado #71: quantos lançamentos da TechMiles chegaram DEPOIS do
  // fechamento no escopo selecionado. Vem de /conciliacao/cartoes, que já
  // conta por cartão+mês; aqui só soma o que está em tela.
  const posFechamento = useMemo(() => cartoes
    .filter(c => !cartaoId || String(c.id) === String(cartaoId))
    .reduce((s, c) => s + (Number(c.qtd_pos_fechamento) || 0), 0), [cartoes, cartaoId]);
  const [resumo, setResumo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [matching, setMatching] = useState(false);
  const [matchResult, setMatchResult] = useState(null);
  const [finalizando, setFinalizando] = useState(false);
  const [selFat, setSelFat] = useState(null);
  const [selXm, setSelXm] = useState(null);
  const [matchEmpresa, setMatchEmpresa] = useState(''); // empresa escolhida no match (chamado #40)
  const [matchOperacao, setMatchOperacao] = useState(''); // operação (centro) escolhida no match — 02/09/2026
  const [selPessoal, setSelPessoal] = useState(new Set()); // seleção múltipla p/ classificar (chamado #22)
  const [pessoalLote, setPessoalLote] = useState(false);
  const [opLote, setOpLote] = useState(''); // operação a aplicar na seleção múltipla
  const [opLoteBusy, setOpLoteBusy] = useState(false);
  const [anteciparBusy, setAnteciparBusy] = useState(false); // antecipação de parcelas em lote
  const [lancamentoModal, setLancamentoModal] = useState(null); // { cartao_id, mes_referencia, nome_cartao } - chamado #30
  const [avulsoLoteModal, setAvulsoLoteModal] = useState(null); // { linhas } — avulso em lote (03/09/2026)
  const [rateioModal, setRateioModal] = useState(null); // conciliação sendo rateada entre empresas (chamado #55)
  const [detalheXmiles, setDetalheXmiles] = useState(null); // { transacaoId, loading, data }
  const [buscaConc, setBuscaConc] = useState(''); // filtro independente dos conciliados
  const [filtroTipoConc, setFiltroTipoConc] = useState('todos'); // 'todos' | 'pessoal' | 'conciliado'
  // Filtro por coluna Operação (02/09/2026): centro efetivo, ou 'AUTO'/'MANUAL'
  // pra varrer o que ainda está na regra vs o que já foi marcado à mão.
  const [filtroOperacao, setFiltroOperacao] = useState('');
  const [xmFiltroCartao, setXmFiltroCartao] = useState(false); // false = mostra TODOS os cartões no XMiles (default); true = só este cartão
  const [incluirNegociacao, setIncluirNegociacao] = useState(false); // mostra cartões em negociação na conciliação
  // Chamados #6/#29/#33/#36: cartão INATIVO some por padrão (é o que o #33
  // pediu), mas 293 dos 382 cartões estão INATIVO e 26 dos 46 titulares não têm
  // nenhum fora desse status — daí "não há cartões" pro PETERSON, ADRIANO,
  // BRUNO, RAFAEL. O toggle devolve o acesso sem desfazer o #33.
  const [incluirInativos, setIncluirInativos] = useState(false);
  const [cartoesOcultos, setCartoesOcultos] = useState(0); // quantos o filtro de status escondeu
  const [filtroDiaVenc, setFiltroDiaVenc] = useState(''); // filtra dropdown de cartões por dia de vencimento (1-31)
  const [buscaXm, setBuscaXm] = useState(''); // busca textual exclusiva do painel XMiles
  const [editModal, setEditModal] = useState(null); // { transacao } - editar cartão de XMiles
  const [todosCartoes, setTodosCartoes] = useState([]); // todos cartões para o modal de edição
  const [pdfTotalFatura, setPdfTotalFatura] = useState(null); // valor total da fatura do PDF (chamado #24)

  // Pre-filtro vindo de outra página (ex: Contas a Pagar → "Conciliar")
  useEffect(() => {
    const nav = localStorage.getItem('conciliacao_prefiltro');
    if (nav) {
      try {
        const { cartao_id, titular_id, mes: mesPre } = JSON.parse(nav);
        if (cartao_id) setCartaoId(String(cartao_id));
        if (titular_id && !titularRestrito) setTitularId(String(titular_id));
        if (mesPre) setMes(mesPre);
      } catch {}
      localStorage.removeItem('conciliacao_prefiltro');
    }
  }, [titularRestrito]);

  // Load titulares once
  useEffect(() => {
    // com_cartao_ativo: só titular com cartão ativo entra no seletor (o servidor
    // decide; antes o corte era total_cartoes > 0, que deixava passar titular
    // cujos cartões já foram todos pra negociação).
    // Com "Incluir inativos" ligado o corte cai junto: senão os 26 titulares
    // 100% inativos continuavam fora do seletor e o toggle não alcançava eles.
    const q = incluirInativos ? '' : '?com_cartao_ativo=true';
    fetch(`${API}/titulares${q}`, { headers: authHeaders() }).then(r => r.json()).then(d => setTitulares(Array.isArray(d) ? d : [])).catch(console.error);
  }, [incluirInativos]);

  // Load all cartões for the edit modal (cartão selector)
  //
  // Chamado #15: `status=ATIVO` aqui era allowlist e trazia 79 dos 382 cartões.
  // Como o filtro "Vence dia N" lê `data_vencimento` DESTA lista, todo cartão
  // SUSPENSO ou INATIVO caía fora do filtro por não estar nela — era o "aparece
  // alguns cartões, mas existem muitos que não estão aparecendo". Agora a régua
  // de status é a do servidor (lib/status-cartao), a mesma das outras listas, e
  // o toggle de inativos vale aqui também.
  useEffect(() => {
    const p = new URLSearchParams({ limit: '500' });
    if (incluirInativos) p.set('incluir_inativos', 'true');
    fetch(`${API}/cartoes?${p}`, { headers: authHeaders() }).then(r => r.json()).then(d => {
      const list = Array.isArray(d) ? d : (Array.isArray(d?.cartoes) ? d.cartoes : []);
      setTodosCartoes(list);
    }).catch(console.error);
  }, [incluirInativos]);

  const loadResumo = useCallback(() => {
    const q = titularRestrito ? `?titular_id=${titularRestrito}` : '';
    fetch(`${API}/conciliacao${q}`).then(r => r.json()).then(setResumo).catch(console.error);
  }, [titularRestrito]);

  // Load cartoes with transactions, filtered by titular
  const loadCartoes = useCallback(() => {
    const params = new URLSearchParams();
    if (mes) params.set('mes_referencia', mes);
    if (titularId) params.set('titular_id', titularId);
    // Chamado #54: o dropdown segue o mesmo toggle da lista — sem isso, cartão
    // em negociação (ex.: SUSPENSO + crise) nunca era selecionável pra conciliar.
    if (incluirNegociacao) params.set('incluir_negociacao', 'true');
    if (incluirInativos) params.set('incluir_inativos', 'true');
    fetch(`${API}/conciliacao/cartoes?${params}`)
      .then(r => r.json()).then(d => {
        // Aceita o formato novo ({ cartoes, ocultos }) e o array puro antigo —
        // front e API sobem na mesma imagem, mas um cache de build velho não
        // pode zerar a tela.
        const lista = Array.isArray(d) ? d : (Array.isArray(d?.cartoes) ? d.cartoes : []);
        setCartoes(lista);
        setCartoesOcultos(Array.isArray(d) ? 0 : (d?.ocultos?.inativos || 0));
      }).catch(console.error);
  }, [mes, titularId, incluirNegociacao, incluirInativos]);

  // Reset cartao when titular changes
  useEffect(() => { setCartaoId(''); }, [titularId]);

  const loadTransacoes = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (cartaoId) params.set('cartao_id', cartaoId);
    if (titularId) params.set('titular_id', titularId);
    if (mes) params.set('mes_referencia', mes);
    // Quando o toggle estiver OFF (default), backend NÃO filtra XMiles por cartão — mostra todos
    if (!xmFiltroCartao) params.set('xmiles_all_cartoes', 'true');
    if (incluirNegociacao) params.set('incluir_negociacao', 'true');
    // Sem isto, escolher um cartão inativo no dropdown devolvia lista vazia (#36).
    if (incluirInativos) params.set('incluir_inativos', 'true');
    fetch(`${API}/conciliacao/transacoes?${params}`)
      .then(r => r.json()).then(d => { setData(d); setLoading(false); })
      .catch(e => { console.error(e); setLoading(false); });
  }, [cartaoId, titularId, mes, xmFiltroCartao, incluirNegociacao, incluirInativos]);

  useEffect(() => { loadResumo(); }, [loadResumo]);
  useEffect(() => { loadCartoes(); }, [loadCartoes]);
  useEffect(() => { loadTransacoes(); }, [loadTransacoes]);

  const cartaoEspecifico = cartaoId && !cartaoId.startsWith('name:') ? cartaoId : null;
  const [openFinance, setOpenFinance] = useState(null);
  const [pedindoAtualizacao, setPedindoAtualizacao] = useState(false);
  const [enviandoPdf, setEnviandoPdf] = useState(false);
  const pdfInputRef = useRef(null);
  const loadOpenFinance = useCallback(() => {
    if (!cartaoEspecifico) { setOpenFinance(null); return; }
    fetch(`${API}/cartoes/${cartaoEspecifico}/open-finance`, { headers: authHeaders() })
      .then(r => (r.ok ? r.json() : null)).then(setOpenFinance).catch(() => setOpenFinance(null));
  }, [cartaoEspecifico]);
  useEffect(() => { loadOpenFinance(); }, [loadOpenFinance]);
  useEffect(() => {
    if (!openFinance || !openFinance.aguardando) return undefined;
    const t = setTimeout(() => { loadOpenFinance(); loadTransacoes(); }, 20000);
    return () => clearTimeout(t);
  }, [openFinance, loadOpenFinance, loadTransacoes]);

  const solicitarAtualizacao = async () => {
    setPedindoAtualizacao(true);
    try {
      const r = await fetch(`${API}/cartoes/${cartaoEspecifico}/open-finance/atualizar`, { method: 'POST', headers: authHeaders() });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      loadOpenFinance();
    } catch (e) { alert('Não foi possível pedir a atualização: ' + e.message); }
    setPedindoAtualizacao(false);
  };

  const enviarPdf = async (arquivo) => {
    if (!arquivo) return;
    setEnviandoPdf(true);
    try {
      const form = new FormData();
      form.append('cartao_id', cartaoEspecifico);
      form.append('mes_referencia', mes);
      form.append('arquivo', arquivo);
      const r = await fetch(`${API}/faturas-v2`, { method: 'POST', headers: authHeaders(), body: form });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      alert('PDF enviado. A leitura da fatura roda em segundo plano — as linhas aparecem aqui quando terminar.');
      setTimeout(loadTransacoes, 15000);
    } catch (e) { alert('Erro ao enviar o PDF: ' + e.message); }
    setEnviandoPdf(false);
    if (pdfInputRef.current) pdfInputRef.current.value = '';
  };

  // Chamado #48: o KPI "Sem Vínculo" vem do backend (GET /pendencias-vinculo)
  // com os mesmos filtros da tela — cobre XMILES_PARC + XM_PRODUCAO + MANUAL,
  // não só a lista XMiles carregada. Recarrega junto com loadTransacoes: o
  // efeito depende de `data`, que muda a cada reload das transações.
  const [pendVinculo, setPendVinculo] = useState(null);
  const loadPendenciasVinculo = useCallback(() => {
    const params = new URLSearchParams();
    if (mes) params.set('mes_referencia', mes);
    if (cartaoId && /^\d+$/.test(cartaoId)) params.set('cartao_id', cartaoId);
    if (titularId) params.set('titular_id', titularId);
    fetch(`${API}/conciliacao/pendencias-vinculo?${params}`)
      .then(r => r.json())
      .then(d => setPendVinculo(d && d.resumo ? d.resumo : null))
      .catch(() => setPendVinculo(null));
  }, [mes, cartaoId, titularId]);
  useEffect(() => { loadPendenciasVinculo(); }, [loadPendenciasVinculo, data]);

  // Valor total do PDF da fatura do cartão selecionado (chamado #24) — pra comparar
  // com o total de "Todos". Só faz sentido com um cartão específico.
  useEffect(() => {
    const cid = cartaoId && !cartaoId.startsWith('name:') ? parseInt(cartaoId) : null;
    if (!cid || !mes) { setPdfTotalFatura(null); return; }
    fetch(`${API}/faturas-v2?cartao_id=${cid}`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => {
        const fs = Array.isArray(d) ? d : (d.data || []);
        const f = fs.find(x => x.mes_referencia === mes);
        setPdfTotalFatura(f && f.valor_total != null ? parseFloat(f.valor_total) : null);
      })
      .catch(() => setPdfTotalFatura(null));
  }, [cartaoId, mes]);

  const shiftMes = (delta) => {
    const [y, m] = mes.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setMes(d.toISOString().slice(0, 7));
  };

  // Separate transactions into matched and unmatched, apply text search
  const { faturaUnmatched, xmilesUnmatched, sugestoes, confirmados, totaisTipo } = useMemo(() => {
    if (!data) return { faturaUnmatched: [], xmilesUnmatched: [], sugestoes: [], confirmados: [], totaisTipo: { totalPessoal: 0, totalConciliado: 0, qtdPessoal: 0, qtdConciliado: 0 } };
    const b = busca.toLowerCase().trim();
    const matchText = (t) => !b || (t.descricao || '').toLowerCase().includes(b) ||
      (t.nome_cartao || '').toLowerCase().includes(b) || String(t.valor || '').includes(b);
    const concs = data.conciliacoes || [];
    const allConfirmados = concs.filter(c => c.status !== 'SUGERIDO');
    const bc = buscaConc.toLowerCase().trim();
    let filteredConc = bc ? allConfirmados.filter(c =>
      (c.descricao_fatura || '').toLowerCase().includes(bc) ||
      (c.descricao_xmiles || '').toLowerCase().includes(bc) ||
      (c.cartao || '').toLowerCase().includes(bc) ||
      (c.fatura_nome_cartao || '').toLowerCase().includes(bc) ||
      (c.fatura_titular || '').toLowerCase().includes(bc) ||
      (c.proprietario_cartao || '').toLowerCase().includes(bc) ||
      String(c.valor_fatura || '').includes(bc) ||
      String(c.valor_xmiles || '').includes(bc)
    ) : allConfirmados;

    // Separação pessoal vs conciliado (chamado #3)
    const ehPessoal = (c) => c.status === 'GASTO_PESSOAL';
    const ehConciliado = (c) => c.status === 'CONCILIADO';
    // Chamado #55: uma conciliação pertence a uma empresa se for a empresa única
    // OU se tiver uma parcela de rateio dessa empresa.
    const temEmpresa = (c, e) => c.empresa === e || (c.rateio && (c.rateios || []).some(r => r.empresa === e));
    // Valor que a empresa "e" carrega nesta conciliação (parcela do rateio ou total).
    const valorDaEmpresa = (c, e) => {
      if (c.rateio) return (c.rateios || []).filter(r => r.empresa === e).reduce((s, r) => s + (parseFloat(r.valor) || 0), 0);
      return parseFloat(c.valor_fatura) || 0;
    };
    if (filtroTipoConc === 'pessoal') filteredConc = filteredConc.filter(ehPessoal);
    else if (filtroTipoConc === 'conciliado') filteredConc = filteredConc.filter(ehConciliado);
    else if (EMPRESAS.includes(filtroTipoConc)) filteredConc = filteredConc.filter(c => temEmpresa(c, filtroTipoConc));
    // Filtro por Operação (02/09/2026): combina com os chips acima (E, não OU).
    if (filtroOperacao === 'MANUAL') filteredConc = filteredConc.filter(c => c.fatura_centro_manual);
    else if (filtroOperacao === 'AUTO') filteredConc = filteredConc.filter(c => !c.fatura_centro_manual);
    else if (filtroOperacao) filteredConc = filteredConc.filter(c => c.fatura_centro_efetivo === filtroOperacao);
    // Chamado #53: soma COM SINAL — lançamento negativo (crédito/estorno)
    // conciliado SUBTRAI do totalizador em vez de somar como se fosse gasto.
    const numSigned = (v) => parseFloat(v) || 0;
    const totaisTipo = {
      totalPessoal: allConfirmados.filter(ehPessoal).reduce((s, c) => s + numSigned(c.valor_fatura), 0),
      totalConciliado: allConfirmados.filter(ehConciliado).reduce((s, c) => s + numSigned(c.valor_fatura), 0),
      qtdPessoal: allConfirmados.filter(ehPessoal).length,
      qtdConciliado: allConfirmados.filter(ehConciliado).length,
      // Totais por empresa (chamado #31) — soma dos conciliados de cada empresa.
      // Chamado #55: inclui a PARCELA de cada rateio (não o valor cheio), pra o
      // saldo por empresa refletir só o que pertence a ela.
      porEmpresa: EMPRESAS.reduce((acc, e) => {
        const its = allConfirmados.filter(c => temEmpresa(c, e));
        acc[e] = { total: its.reduce((s, c) => s + valorDaEmpresa(c, e), 0), qtd: its.length };
        return acc;
      }, {}),
      // Contagem/total por centro EFETIVO (02/09/2026) — alimenta o filtro de
      // Operação. Sobre o conjunto cheio, como os chips: o filtro mostra quanto
      // existe de cada centro antes de aplicá-lo.
      porOperacao: allConfirmados.reduce((acc, c) => {
        const k = c.fatura_centro_efetivo || 'SEM_CENTRO';
        (acc[k] = acc[k] || { qtd: 0, total: 0 });
        acc[k].qtd += 1; acc[k].total += numSigned(c.valor_fatura);
        return acc;
      }, {}),
      qtdManuais: allConfirmados.filter(c => c.fatura_centro_manual).length,
      qtdTotalConf: allConfirmados.length,
    };

    // XMiles filter: toggle por cartão + busca textual própria do painel
    const cartaoIdNum = cartaoId && !cartaoId.startsWith('name:') ? parseInt(cartaoId) : null;
    const bxm = buscaXm.toLowerCase().trim();
    const matchXm = (x) => !bxm || (x.descricao || '').toLowerCase().includes(bxm) ||
      (x.nome_cartao || '').toLowerCase().includes(bxm) ||
      String(x.valor || '').includes(bxm) ||
      (x.categoria || '').toLowerCase().includes(bxm) ||
      (x.fidelizado || '').toLowerCase().includes(bxm) || // chamado #60
      (x.parcela || '').toLowerCase().includes(bxm);
    const xmilesBase = (data.xmiles || []).filter(x => !x._conciliado && matchText(x) && matchXm(x));
    const xmilesFiltrado = (xmFiltroCartao && cartaoIdNum)
      ? xmilesBase.filter(x => x.cartao_id === cartaoIdNum)
      : xmilesBase;

    // Filtro por dia de vencimento (chamado #15): limita TODAS as listas aos
    // cartões que vencem no dia escolhido — antes só limitava o dropdown.
    const diaVencSel = filtroDiaVenc ? parseInt(filtroDiaVenc, 10) : null;
    const cardIdsNoDia = diaVencSel
      ? new Set((todosCartoes || []).filter(tc => diaDoVencimento(tc.data_vencimento) === diaVencSel).map(tc => tc.id))
      : null;
    const matchDiaVenc = (row) => !cardIdsNoDia || cardIdsNoDia.has(row.cartao_id);
    // Quando um cartão específico está filtrado, as Possíveis Matches (sugestões)
    // devem mostrar só as desse cartão (chamado #19).
    const matchCartaoSel = (row) => !cartaoIdNum || row.cartao_id === cartaoIdNum;

    return {
      faturaUnmatched: (data.fatura || []).filter(f => !f._conciliado && !f.coberta && matchText(f) && matchDiaVenc(f)),
      xmilesUnmatched: xmilesFiltrado.filter(matchDiaVenc),
      sugestoes: concs.filter(c => c.status === 'SUGERIDO' && matchDiaVenc(c) && matchCartaoSel(c)),
      confirmados: filteredConc.filter(matchDiaVenc),
      totaisTipo,
      // Chamado #48: o KPI "Sem Vínculo" saiu daqui — agora vem do backend
      // (pendVinculo, via GET /pendencias-vinculo), cobrindo todas as origens.
    };
  }, [data, busca, buscaXm, buscaConc, xmFiltroCartao, cartaoId, filtroTipoConc, filtroOperacao, filtroDiaVenc, todosCartoes]);

  // Chamado #1: escolher "Vence dia N" tem que estreitar TAMBÉM o seletor de
  // titulares — era a queixa literal ("nos outros filtros ainda aparece todos os
  // cartões e não só o daquele dia"). O dropdown de cartões e as listas já
  // seguiam o dia; o de titulares continuava com os 46 nomes, e escolher um sem
  // cartão naquele dia esvaziava a tela sem explicar por quê.
  const titularesNoDia = useMemo(() => {
    if (!filtroDiaVenc) return titulares;
    const dia = parseInt(filtroDiaVenc, 10);
    const ids = new Set(
      (todosCartoes || [])
        .filter(c => diaDoVencimento(c.data_vencimento) === dia)
        .map(c => c.titular_id)
    );
    return titulares.filter(t => ids.has(t.id));
  }, [titulares, todosCartoes, filtroDiaVenc]);

  // Se o titular selecionado não tem cartão no dia escolhido, solta a seleção —
  // deixá-la presa mostrava uma tela vazia com dois filtros que se contradizem.
  useEffect(() => {
    if (!filtroDiaVenc || !titularId) return;
    if (!titularesNoDia.some(t => String(t.id) === String(titularId))) setTitularId('');
  }, [filtroDiaVenc, titularId, titularesNoDia]);

  const exportarExcel = async () => {
    try {
      const p = new URLSearchParams();
      if (cartaoId && !cartaoId.startsWith('name:')) p.set('cartao_id', cartaoId);
      if (titularId) p.set('titular_id', titularId);
      if (mes) p.set('mes_referencia', mes);
      p.set('tipo', filtroTipoConc);
      const res = await fetch(`${API}/conciliacao/exportar?${p}`, { headers: authHeaders() });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || `HTTP ${res.status}`); }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `conciliados_${filtroTipoConc}_${mes || 'geral'}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { alert('Erro ao exportar: ' + e.message); }
  };

  const autoMatch = async () => {
    setMatching(true);
    setMatchResult(null);
    try {
      const body = {};
      if (cartaoId) body.cartao_id = parseInt(cartaoId);
      if (mes) body.mes_referencia = mes;
      if (incluirNegociacao) body.incluir_negociacao = true;
      const res = await fetch(`${API}/conciliacao/auto-match`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() }, body: JSON.stringify(body),
      });
      const r = await res.json();
      if (r.error) { alert(r.error); } else { setMatchResult(r); }
      loadTransacoes();
      loadResumo();
      loadCartoes();
    } catch (e) { console.error(e); }
    setMatching(false);
  };

  // Finaliza a conciliação do cartão selecionado no mês (chamado #17):
  // apaga os lançamentos restantes do manager (XMiles) não conciliados.
  const finalizar = async () => {
    if (!cartaoId || cartaoId.startsWith('name:')) {
      alert('Selecione um cartão específico para finalizar a conciliação.');
      return;
    }
    const nomeCartao = cartoes.find(c => String(c.id) === cartaoId)?.nome_cartao || 'este cartão';
    if (!window.confirm(`Finalizar a conciliação de "${nomeCartao}" em ${mes}?\n\nOs lançamentos ficam BLOQUEADOS para edição (você pode Desfazer depois). Isso também limpa os lançamentos restantes do XMiles ainda não conciliados deste cartão/mês.`)) return;
    setFinalizando(true);
    try {
      const postFinalizar = (forcar) => fetch(`${API}/conciliacao/finalizar`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ cartao_id: parseInt(cartaoId), mes_referencia: mes, ...(forcar ? { forcar: true } : {}) }),
      });
      let res = await postFinalizar(false);
      let r = await res.json();
      // Chamado #50: gastos sem vínculo → 409. Soft (pode_forcar): confirma a
      // contagem/valor e reenvia com forcar. Estrito: explica e não finaliza.
      if (res.status === 409 && r.pode_forcar) {
        const ok = window.confirm(`${r.sem_vinculo} gasto(s) sem vínculo (${fmt(r.valor_sem_vinculo)}) neste cartão/mês.\n\nFinalizar mesmo assim?`);
        if (!ok) { setFinalizando(false); return; }
        res = await postFinalizar(true);
        r = await res.json();
      } else if (res.status === 409) {
        alert(`Finalização bloqueada: ${r.sem_vinculo} gasto(s) sem vínculo (${fmt(r.valor_sem_vinculo)}).\n\nO modo estrito (VINCULO_ESTRITO) exige vincular todos os gastos antes de finalizar.`);
        setFinalizando(false);
        return;
      }
      if (!res.ok) throw new Error(r.error || 'Falha');
      loadTransacoes(); loadResumo(); loadCartoes();
    } catch (e) { alert('Erro ao finalizar: ' + e.message); }
    setFinalizando(false);
  };

  // Chamado #43: destrava a conciliação do cartão/mês (volta a editar).
  const desfazerFinalizacao = async () => {
    if (!cartaoId || cartaoId.startsWith('name:')) return;
    setFinalizando(true);
    try {
      const res = await fetch(`${API}/conciliacao/desfazer-finalizacao`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ cartao_id: parseInt(cartaoId), mes_referencia: mes }),
      });
      if (!res.ok) { const r = await res.json().catch(() => ({})); throw new Error(r.error || 'Falha'); }
      loadTransacoes(); loadResumo(); loadCartoes();
    } catch (e) { alert('Erro ao desfazer: ' + e.message); }
    setFinalizando(false);
  };

  const confirmar = async (id) => {
    await fetch(`${API}/conciliacao/confirmar/${id}`, { method: 'POST', headers: authHeaders() });
    loadTransacoes(); loadResumo();
  };

  const rejeitar = async (id) => {
    await fetch(`${API}/conciliacao/rejeitar/${id}`, { method: 'POST', headers: authHeaders() });
    loadTransacoes(); loadResumo();
  };

  // Pré-seleção da operação no casamento: só a escolha à mão que a linha JÁ
  // tem. O resto fica em "auto" — que não grava nada e deixa a regra valer,
  // inclusive o centro padrão do cartão. Antes pré-selecionava uma sugestão
  // calculada aqui, e confirmar o casamento a gravava como manual.
  useEffect(() => {
    if (!selFat) { setMatchOperacao(''); return; }
    const t = (data?.fatura || []).find(x => x.id === selFat);
    setMatchOperacao(t && t.centro_fechamento && OPERACOES.some(o => o.v === t.centro_fechamento)
      ? t.centro_fechamento : '');
  }, [selFat, data]);
  // O que a regra dá pra linha selecionada — rótulo do "auto" no seletor.
  const centroRegraSel = selFat ? ((data?.fatura || []).find(x => x.id === selFat)?.centro_regra || null) : null;

  const matchManual = async () => {
    if (!selFat || !selXm) return;
    await fetch(`${API}/conciliacao/manual`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ transacao_fatura_id: selFat, transacao_xmiles_id: selXm,
        empresa: matchEmpresa || null, operacao: matchOperacao || null }),
    });
    setSelFat(null); setSelXm(null); setMatchEmpresa(''); setMatchOperacao('');
    loadTransacoes(); loadResumo(); loadCartoes();
  };

  const desfazer = async (id) => {
    await fetch(`${API}/conciliacao/${id}`, { method: 'DELETE', headers: authHeaders() });
    loadTransacoes(); loadResumo();
  };

  // Marca/troca a empresa de um conciliado direto na lista (chamado #31).
  // Chamado #55: escolher 'RATEIO' abre o modal; empresa única desfaz o rateio.
  const setEmpresaConc = async (concId, empresa) => {
    if (empresa === 'RATEIO') { const c = confirmados.find(x => x.id === concId); setRateioModal(c || { id: concId }); return; }
    try {
      const r = await fetch(`${API}/conciliacao/${concId}/empresa`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ empresa: empresa || null }),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || `HTTP ${r.status}`); }
      loadTransacoes();
    } catch (e) { alert('Erro ao definir empresa: ' + e.message); }
  };

  // Operação (02/09/2026): marca/troca o centro de fechamento de um conciliado
  // direto na lista. Vazio = volta pra regra automática do relatório.
  const setOperacaoConc = async (concId, operacao) => {
    try {
      const r = await fetch(`${API}/conciliacao/${concId}/operacao`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ operacao: operacao || null }),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || `HTTP ${r.status}`); }
      loadTransacoes();
    } catch (e) { alert('Erro ao definir operação: ' + e.message); }
  };

  // Operação em lote nos checkboxes da lista Sem match (mesma seleção do
  // "Marcar como Pessoal") — taggeia N linhas de fatura de uma vez.
  const aplicarOperacaoLote = async () => {
    const ids = [...selPessoal];
    if (!ids.length || !opLote) return;
    setOpLoteBusy(true);
    try {
      const r = await fetch(`${API}/conciliacao/operacao-lote`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ transacao_ids: ids, operacao: opLote }),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || `HTTP ${r.status}`); }
      setSelPessoal(new Set()); setOpLote('');
      loadTransacoes();
    } catch (e) { alert('Erro ao classificar operação em lote: ' + e.message); }
    setOpLoteBusy(false);
  };

  // Chamado #55: aplica o rateio (lista de { empresa, valor }) numa conciliação.
  const salvarRateio = async (concId, empresas) => {
    const r = await fetch(`${API}/conciliacao/${concId}/ratear`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ empresas }),
    });
    if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || `HTTP ${r.status}`); }
    setRateioModal(null); loadTransacoes();
  };

  // Chamado #47: define o vínculo de rastreabilidade de um gasto. ESTOQUE/EMISSAO
  // pedem o identificador externo (lote no TechMiles / id da emissão); os demais
  // são classificação direta. Vazio limpa o vínculo (volta a "sem vínculo").
  const setVinculo = async (transacaoId, tipo, refAtual) => {
    const body = { tipo_vinculo: tipo || null };
    if (tipo === 'ESTOQUE' || tipo === 'EMISSAO') {
      const rotulo = tipo === 'ESTOQUE' ? 'lote de estoque no TechMiles' : 'id/localizador da emissão';
      const ref = window.prompt(`Informe o ${rotulo}:`, refAtual || '');
      if (ref == null || !ref.trim()) return; // cancelou
      if (tipo === 'ESTOQUE') body.estoque_ref = ref.trim(); else body.emissao_ref = ref.trim();
    }
    try {
      const r = await fetch(`${API}/conciliacao/transacao/${transacaoId}/vinculo`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(body),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || `HTTP ${r.status}`); }
      loadTransacoes();
    } catch (e) { alert('Erro ao definir vínculo: ' + e.message); }
  };

  const marcarGastoPessoal = async (transacaoId) => {
    await fetch(`${API}/conciliacao/gasto-pessoal`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ transacao_id: transacaoId }),
    });
    loadTransacoes(); loadResumo(); loadCartoes();
  };

  // Seleção múltipla de lançamentos de fatura p/ classificar em lote (chamado #22)
  const togglePessoalSel = (id) => setSelPessoal(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const marcarPessoalLote = async () => {
    const ids = [...selPessoal];
    if (!ids.length) return;
    setPessoalLote(true);
    try {
      await Promise.all(ids.map(id => fetch(`${API}/conciliacao/gasto-pessoal`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ transacao_id: id }),
      })));
      setSelPessoal(new Set());
      loadTransacoes(); loadResumo(); loadCartoes();
    } catch (e) { alert('Erro ao classificar em lote: ' + e.message); }
    setPessoalLote(false);
  };

  // Antecipação de parcelas (02/09/2026): a fatura veio com parcelas FUTURAS
  // (antecipadas) que no XMiles vivem nos meses seguintes e nunca casariam pelo
  // fluxo normal. O server acha a parcela futura correspondente (gabarito = a
  // parcela já conciliada da mesma compra; desempate por número da parcela) e
  // concilia agora, dando baixa. Reversível pelo Desfazer normal.
  const anteciparParcela = async (transacaoId) => {
    try {
      const r = await fetch(`${API}/conciliacao/antecipar`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ transacao_fatura_id: transacaoId }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      loadTransacoes(); loadResumo(); loadCartoes();
    } catch (e) { alert('Antecipação: ' + e.message); }
  };
  const anteciparLote = async () => {
    const ids = [...selPessoal];
    if (!ids.length) return;
    setAnteciparBusy(true);
    const erros = [];
    // Sequencial de propósito: duas linhas iguais não podem disputar a mesma
    // parcela futura — a 1ª concilia e a 2ª já não a vê como candidata.
    for (const id of ids) {
      try {
        const r = await fetch(`${API}/conciliacao/antecipar`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify({ transacao_fatura_id: id }),
        });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      } catch (e) { erros.push(e.message); }
    }
    setSelPessoal(new Set());
    loadTransacoes(); loadResumo(); loadCartoes();
    if (erros.length) alert(`Antecipação: ${ids.length - erros.length} conciliada(s), ${erros.length} falha(s):\n` + erros.slice(0, 5).join('\n'));
    setAnteciparBusy(false);
  };

  const desfazerPessoal = async (transacaoId) => {
    await fetch(`${API}/conciliacao/desfazer-pessoal/${transacaoId}`, { method: 'POST', headers: authHeaders() });
    loadTransacoes(); loadResumo(); loadCartoes();
  };

  const marcarPgtoEstorno = async (transacaoId) => {
    await fetch(`${API}/conciliacao/pgto-estorno`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ transacao_id: transacaoId }),
    });
    loadTransacoes(); loadResumo(); loadCartoes();
  };

  const marcarPgtoEstornoBulk = async () => {
    const body = {};
    if (cartaoId) body.cartao_id = parseInt(cartaoId);
    if (mes) body.mes_referencia = mes;
    const r = await fetch(`${API}/conciliacao/pgto-estorno-bulk`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(body),
    });
    const result = await r.json();
    alert(result.message || `${result.count} transações marcadas`);
    loadTransacoes(); loadResumo(); loadCartoes();
  };

  const [realocacaoResult, setRealocacaoResult] = useState(null); // resultado da realocação no Manager

  const editarCartaoXmiles = async (transacaoId, novoCartaoId, motivo) => {
    try {
      // Tentar realocação completa no Manager (cancela original + recria no cartão correto)
      const r = await fetch(`${API}/xmiles/realocar-cartao/${transacaoId}`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ novo_cartao_id: parseInt(novoCartaoId), motivo }),
      });
      const result = await r.json();
      if (!r.ok) {
        // Se falhou no Manager (ex: sem xmiles_id), tentar só update local
        if (result.error && result.error.includes('não tem referência do Manager')) {
          const r2 = await fetch(`${API}/transacoes/${transacaoId}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ cartao_id: parseInt(novoCartaoId), motivo }),
          });
          const res2 = await r2.json();
          if (!r2.ok) { alert(`Erro: ${res2.error || 'Falha ao editar'}`); return; }
          setEditModal(null);
          loadTransacoes(); loadCartoes();
          return;
        }
        alert(`Erro: ${result.error || 'Falha ao realocar'}`);
        return;
      }
      setEditModal(null);
      setRealocacaoResult(result);
      loadTransacoes(); loadCartoes();
    } catch (e) { alert(`Erro: ${e.message}`); }
  };

  const editarParcelaXmiles = async (transacaoId, { novo_valor, nova_data, motivo }) => {
    try {
      // Edição LOCAL (chamado #41): não passa mais pelo Manager. Atualiza a
      // transação (e a conciliação, se houver).
      const r = await fetch(`${API}/conciliacao/transacao/${transacaoId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ novo_valor, nova_data, motivo }),
      });
      const result = await r.json();
      if (!r.ok) { alert(`Erro: ${result.error || 'Falha ao editar lançamento'}`); return; }
      setEditModal(null);
      // A data de vencimento decide a que fatura a parcela pertence. Se a nova
      // data a jogou para outro mês, ela SAI desta lista — legítimo, mas some
      // da tela do mesmo jeito. Dizer para onde foi é o que diferencia isso de
      // perda de dado (era a queixa original, por outro caminho).
      if (mes && result.mes_referencia && result.mes_referencia !== mes) {
        alert(
          `Lançamento movido para a fatura de ${result.mes_referencia}.\n\n` +
          `Ele saiu desta lista porque a tela está no mês ${mes} — ` +
          `troque o filtro de mês para vê-lo.`
        );
      }
      loadTransacoes(); loadCartoes(); loadResumo();
    } catch (e) { alert(`Erro: ${e.message}`); }
  };

  // Parcelamento local (17/09/2026): transforma o lançamento em N parcelas. O
  // valor/data que estiverem no modal entram como base — é a mesma alteração.
  const parcelarLancamento = async (transacaoId, { quantidade, motivo, novo_valor, nova_data }) => {
    try {
      const r = await fetch(`${API}/conciliacao/transacao/${transacaoId}/parcelar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ quantidade, motivo, novo_valor, nova_data }),
      });
      const result = await r.json();
      if (!r.ok) { alert(`Erro: ${result.error || 'Falha ao parcelar lançamento'}`); return; }
      setEditModal(null);
      // As parcelas futuras nascem em OUTRAS faturas e somem desta lista — dizer
      // para onde foram é o que diferencia isso de perda de dado (mesma queixa
      // que a edição de data já tratou).
      const fora = (result.parcelas || []).filter(p => mes && p.mes_referencia !== mes);
      alert(
        `Lançamento parcelado em ${result.total_parcelas}x.\n\n` +
        (result.parcelas || []).map(p =>
          `${p.numero}/${result.total_parcelas} · ${p.data_vencimento} · ` +
          `R$ ${Number(p.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })} · fatura ${p.mes_referencia}`
        ).join('\n') +
        (fora.length ? `\n\n${fora.length} parcela(s) ficaram em outros meses — troque o filtro de mês para vê-las.` : '')
      );
      loadTransacoes(); loadCartoes(); loadResumo();
    } catch (e) { alert(`Erro: ${e.message}`); }
  };

  const verPdf = (cartaoId) => {
    // window.open não passa pelo interceptador de fetch, então o header
    // Authorization não vai — abria e voltava {"error":"Token não fornecido"}.
    const tk = localStorage.getItem('xm_token') || '';
    if (cartaoId && mes) window.open(`${API}/faturas/pdf/${cartaoId}/${mes}?token=${encodeURIComponent(tk)}`, '_blank');
  };

  const abrirDetalheXmiles = async (transacaoId) => {
    setDetalheXmiles({ transacaoId, loading: true, data: null });
    try {
      const r = await fetch(`${API}/xmiles/detalhe-transacao/${transacaoId}`);
      const d = await r.json();
      setDetalheXmiles({ transacaoId, loading: false, data: d });
    } catch (e) {
      setDetalheXmiles({ transacaoId, loading: false, data: null, error: e.message });
    }
  };

  const btnStyle = (active) => ({
    padding: '6px 12px', borderRadius: 8, border: `1px solid ${active ? colors.primary : colors.border}`,
    background: active ? `${colors.primary}22` : 'transparent', color: active ? colors.primary : colors.textMuted,
    cursor: 'pointer', fontSize: 13, fontWeight: 500,
  });

  const confColor = (c) => c >= 70 ? colors.success : c >= 40 ? colors.warning : colors.danger;

  return (
    <div>
      <PageHeader title="Conciliação" subtitle="Fatura PDF x XMiles Manager"
        onAdd={() => setCentrosOpen(true)} addLabel="Cadastrar operação" />
      <CentrosFechamentoModal isOpen={centrosOpen} onClose={() => setCentrosOpen(false)} onChange={carregarCentros} />

      {/* Modal "Fazer Lançamento" — avulso ou recorrente (chamado #30) */}
      {lancamentoModal && (
        <LancamentoModal
          ctx={lancamentoModal}
          onClose={() => setLancamentoModal(null)}
          onSaved={() => { setLancamentoModal(null); loadTransacoes(); loadResumo(); loadCartoes(); }}
        />
      )}

      {/* Modal "Avulso em lote" — classificação em massa (03/09/2026) */}
      {avulsoLoteModal && (
        <AvulsoLoteModal
          ctx={avulsoLoteModal}
          onClose={() => setAvulsoLoteModal(null)}
          onSaved={(d) => {
            setAvulsoLoteModal(null); setSelPessoal(new Set());
            loadTransacoes(); loadResumo(); loadCartoes();
            if (d?.erros?.length) {
              alert(`Avulso em lote: ${d.criadas} lançada(s), ${d.erros.length} falha(s):\n` + d.erros.slice(0, 5).join('\n'));
            }
          }}
        />
      )}

      {/* Modal de detalhes XMiles */}
      {detalheXmiles && (
        <DetalheXmilesModal
          detalhe={detalheXmiles}
          onClose={() => setDetalheXmiles(null)}
        />
      )}

      {/* Modal de edição XMiles (duas abas: ajustar parcela / trocar cartão) */}
      {editModal && (
        <EditarCartaoModal
          transacao={editModal}
          cartoes={todosCartoes}
          onClose={() => setEditModal(null)}
          onSave={editarCartaoXmiles}
          onSaveParcela={editarParcelaXmiles}
          onParcelar={parcelarLancamento}
        />
      )}

      {/* Modal de resultado da realocação no Manager */}
      {realocacaoResult && (
        <RealocacaoResultModal
          data={realocacaoResult}
          filtro={{ titularId, incluirInativos, incluirNegociacao }}
          onClose={() => setRealocacaoResult(null)}
        />
      )}

      {/* Modal de rateio entre empresas (chamado #55) */}
      {rateioModal && (
        <RateioModal
          conc={rateioModal}
          onClose={() => setRateioModal(null)}
          onSave={salvarRateio}
          onCancelarRateio={async () => {
            // Desfazer o rateio = definir "sem empresa" (o PATCH /empresa já
            // apaga as parcelas). A linha volta ao select normal e o usuário
            // escolhe UMA empresa do jeito de sempre.
            await setEmpresaConc(rateioModal.id, '');
            setRateioModal(null);
          }}
        />
      )}

      {/* KPIs — escondidos no modo leitura (investidor vê só os conciliados) */}
      {!readOnly && (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16, marginBottom: 24 }}>
        <StatCard label="Conciliados" value={resumo?.conciliados ?? '-'} color={colors.success} />
        <StatCard label="Pendentes" value={resumo?.pendentes ?? '-'} color={colors.warning} />
        <StatCard label="Divergentes" value={resumo?.divergentes ?? '-'} color={colors.danger}
          subtitle={resumo?.total_divergencia ? `Dif: ${fmt(resumo.total_divergencia)}` : ''} />
        <StatCard label="Sem Match Fatura" value={faturaUnmatched.length} color={colors.info} />
        {/* Chamados #47/#48: gastos sem vínculo no escopo atual — contagem do
            backend (pendencias-vinculo), cobrindo XMILES_PARC + XM_PRODUCAO + MANUAL */}
        <StatCard label="Sem Vínculo"
          value={pendVinculo ? Number(pendVinculo.sem_vinculo) : '-'}
          color={Number(pendVinculo?.sem_vinculo || 0) > 0 ? colors.danger : colors.success}
          subtitle={Number(pendVinculo?.sem_vinculo || 0) > 0
            ? `${fmt(pendVinculo.valor_sem_vinculo)} não rastreados`
            : 'gastos não rastreados'} />
      </div>
      )}

      {/* Filters */}
      <div style={{
        display: 'flex', gap: 12, alignItems: 'center', marginBottom: 20,
        background: colors.card, padding: '12px 16px', borderRadius: 12, border: `1px solid ${colors.border}`,
        flexWrap: 'wrap',
      }}>
        <button onClick={() => shiftMes(-1)} style={btnStyle(false)}><ChevronLeft size={16} /></button>
        <input type="month" value={mes} onChange={e => setMes(e.target.value)}
          style={{
            padding: '6px 12px', borderRadius: 8, border: `1px solid ${colors.border}`,
            background: colors.bg, color: colors.text, fontSize: 14, colorScheme: 'dark',
          }} />
        <button onClick={() => shiftMes(1)} style={btnStyle(false)}><ChevronRight size={16} /></button>
        <button onClick={() => setMes(mesAtual())} style={btnStyle(mes === mesAtual())}>Mês Atual</button>

        <div style={{ width: 1, height: 24, background: colors.border, margin: '0 4px' }} />

        {/* Investidor: titular travado no dele (sem seletor). Gestor: seletor livre. */}
        {!titularRestrito && (
          <SearchableSelect
            value={titularId}
            onChange={val => setTitularId(val)}
            placeholder="Todos os titulares"
            style={{ width: 200 }}
            options={titularesNoDia.map(t => ({ value: String(t.id), label: t.nome }))}
          />
        )}

        {/* Filtro por dia de vencimento — limita o dropdown E as listas exibidas (chamados #1, #15) */}
        <select
          value={filtroDiaVenc}
          onChange={e => { setFiltroDiaVenc(e.target.value); setCartaoId(''); }}
          title="Filtra os cartões pelo dia de vencimento"
          style={{
            padding: '6px 10px', borderRadius: 8, border: `1px solid ${filtroDiaVenc ? colors.primary : colors.border}`,
            background: colors.bg, color: filtroDiaVenc ? colors.primary : colors.text, fontSize: 13, cursor: 'pointer',
          }}>
          <option value="">Venc: todos os dias</option>
          {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
            <option key={d} value={d}>Vence dia {d}</option>
          ))}
        </select>

        <SearchableSelect
          value={cartaoId}
          onChange={val => setCartaoId(val)}
          placeholder="Todos os cartões"
          style={{ width: 320 }}
          options={cartoes.filter(c => {
            if (!filtroDiaVenc) return true;
            // dia de vencimento vem de todosCartoes (tem data_vencimento)
            const full = todosCartoes.find(tc => tc.id === c.id);
            return diaDoVencimento(full?.data_vencimento) === parseInt(filtroDiaVenc, 10);
          }).map(c => ({
            value: c.id ? String(c.id) : `name:${c.nome_cartao}`,
            // Chamado #71: "⚠ N após fechamento" marca o cartão que recebeu
            // lançamento da TechMiles depois de a conciliação ser finalizada.
            // Sem isso ele aparecia como XM:0 e parecia não ter nada.
            // A lista traz todos os cartões vivos; sem pendência no mês o
            // cartão aparece como "✓ conciliado" (antes simplesmente sumia).
            label: `${c.nome_cartao}${c.titular_nome && c.titular_nome !== '-' ? ` (${c.titular_nome})` : ''} — `
              + (Number(c.qtd_fatura) === 0 && Number(c.qtd_xmiles) === 0 && !(Number(c.qtd_pos_fechamento) > 0)
                ? '✓ conciliado'
                : `F:${c.qtd_fatura} / XM:${c.qtd_xmiles}`)
              + (Number(c.qtd_pos_fechamento) > 0 ? ` ⚠ ${c.qtd_pos_fechamento} após fechamento` : ''),
          }))}
        />

        <div style={{ width: 1, height: 24, background: colors.border, margin: '0 4px' }} />

        <input
          value={busca} onChange={e => setBusca(e.target.value)}
          placeholder="Buscar descrição..."
          style={{
            padding: '6px 12px', borderRadius: 8, border: `1px solid ${colors.border}`,
            background: colors.bg, color: colors.text, fontSize: 13, width: 180,
            outline: 'none', boxSizing: 'border-box',
          }}
          onFocus={e => { e.target.style.borderColor = colors.primary; }}
          onBlur={e => { e.target.style.borderColor = colors.border; }}
        />

        <button
          onClick={() => setIncluirNegociacao(v => !v)}
          title="Mostra também cartões em negociação (ex: titulares 100% em negociação como PETERSON)"
          style={{
            padding: '6px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
            border: `1px solid ${incluirNegociacao ? '#f97316' : colors.border}`,
            background: incluirNegociacao ? '#f9731622' : 'transparent',
            color: incluirNegociacao ? '#f97316' : colors.textMuted,
          }}>
          {incluirNegociacao ? '✓ Em negociação' : '+ Em negociação'}
        </button>

        {/* Chamados #6/#29/#33/#36: cartão inativo continua fora por padrão
            (#33), mas agora a tela diz quantos escondeu e o toggle os traz de
            volta — 26 dos 46 titulares não têm nenhum cartão fora de INATIVO. */}
        <button
          onClick={() => { setIncluirInativos(v => !v); setCartaoId(''); }}
          title="Cartões INATIVO/CANCELADO ficam fora por padrão. Ligue para alcançar titulares cujos cartões já foram todos desativados (PETERSON, ADRIANO, BRUNO, RAFAEL...)."
          style={{
            padding: '6px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
            border: `1px solid ${incluirInativos ? '#f97316' : colors.border}`,
            background: incluirInativos ? '#f9731622' : 'transparent',
            color: incluirInativos ? '#f97316' : colors.textMuted,
          }}>
          {incluirInativos ? '✓ Inativos' : '+ Inativos'}
        </button>

        {!incluirInativos && cartoesOcultos > 0 && (
          <button
            onClick={() => { setIncluirInativos(true); setCartaoId(''); }}
            title="Mostrar os cartões inativos que este filtro escondeu"
            style={{
              padding: '6px 10px', borderRadius: 8, cursor: 'pointer', fontSize: 12,
              border: `1px dashed ${colors.border}`, background: 'transparent', color: colors.textMuted,
            }}>
            {cartoesOcultos} cartão(ões) oculto(s) por status — mostrar
          </button>
        )}

        <div style={{ flex: 1 }} />

        {/* Ações de mutação — escondidas no modo leitura (investidor) */}
        {!readOnly && (
        <>
        {/* Auto Match e Fazer Lançamento só quando editável (não finalizado) — chamado #43 */}
        {editavel && (
        <>
        <Button onClick={autoMatch} disabled={matching} color={colors.primary} size="sm" icon={Zap}>
          {matching ? 'Processando...' : 'Auto Match'}
        </Button>

        {/* Fazer lançamento manual / recorrente (chamado #30) — só com cartão específico */}
        <Button
          onClick={() => {
            if (!cartaoId || cartaoId.startsWith('name:')) { alert('Selecione um cartão específico para fazer um lançamento.'); return; }
            setLancamentoModal({
              cartao_id: cartaoId, mes_referencia: mes,
              nome_cartao: cartoes.find(c => String(c.id) === cartaoId)?.nome_cartao || '',
            });
          }}
          disabled={!cartaoId || cartaoId.startsWith('name:')}
          color={colors.primary}
          size="sm"
          icon={PlusCircle}
          title={!cartaoId || cartaoId.startsWith('name:')
            ? 'Selecione um cartão específico para lançar'
            : 'Criar um lançamento manual (avulso ou recorrente) nesta fatura'}>
          Fazer Lançamento
        </Button>
        </>
        )}

        {cartaoEspecifico && (
        <>
        <input ref={pdfInputRef} type="file" accept="application/pdf" style={{ display: 'none' }}
          onChange={(e) => enviarPdf(e.target.files && e.target.files[0])} />
        <Button onClick={() => pdfInputRef.current && pdfInputRef.current.click()} disabled={enviandoPdf}
          color={colors.info} size="sm" icon={Upload}
          title={`Enviar o PDF da fatura de ${mes} deste cartão`}>
          {enviandoPdf ? 'Enviando...' : 'Enviar PDF'}
        </Button>
        </>
        )}

        {/* Finalizar / Desfazer conciliação do cartão selecionado (chamados #17 e #43) — só com cartão específico */}
        {finalizada ? (
        <Button
          onClick={desfazerFinalizacao}
          disabled={finalizando || !cartaoId || cartaoId.startsWith('name:')}
          color={colors.warning}
          size="sm"
          icon={Undo2}
          title="Reabre a conciliação e libera novamente a edição dos lançamentos">
          {finalizando ? 'Desfazendo...' : 'Desfazer Conciliação'}
        </Button>
        ) : (
        <Button
          onClick={finalizar}
          disabled={finalizando || !cartaoId || cartaoId.startsWith('name:')}
          color={colors.success}
          size="sm"
          icon={CheckCircle2}
          title={!cartaoId || cartaoId.startsWith('name:')
            ? 'Selecione um cartão específico para finalizar'
            : 'Bloqueia a edição dos lançamentos e limpa os restantes do manager (XMiles) deste cartão/mês'}>
          {finalizando ? 'Finalizando...' : 'Finalizar Conciliação'}
        </Button>
        )}
        </>
        )}

        {cartaoEspecifico && openFinance && openFinance.open_finance && (
        <Button onClick={solicitarAtualizacao} disabled={pedindoAtualizacao || openFinance.aguardando}
          color={colors.primary} size="sm" icon={RefreshCw}
          title="Pede à Pluggy uma coleta nova dos dados desta conta no banco">
          {pedindoAtualizacao ? 'Pedindo...'
            : openFinance.aguardando
              ? `Aguardando atualização desde ${new Date(openFinance.atualizacao_pedida_em).toLocaleString('pt-BR')}`
              : 'Solicitar atualização'}
        </Button>
        )}
      </div>

      {/* Match result banner */}
      {matchResult && (
        <div style={{
          background: `${colors.success}11`, border: `1px solid ${colors.success}44`,
          borderRadius: 12, padding: 16, marginBottom: 20,
        }}>
          <div style={{ fontWeight: 600, color: colors.success, marginBottom: 8 }}>Conciliação automática executada</div>
          <div style={{ display: 'flex', gap: 24, fontSize: 14, color: colors.text, flexWrap: 'wrap' }}>
            <div>Conciliados: <strong>{matchResult.matched}</strong></div>
            <div>Sugeridos: <strong>{matchResult.suggestions}</strong></div>
            <div>Sem match (Fatura): <strong>{matchResult.sem_match_fatura}</strong></div>
            <div>Sem match (XMiles): <strong>{matchResult.sem_match_xmiles}</strong></div>
          </div>
        </div>
      )}

      {/* Spinner só no 1º load (chamado #20): em reloads após ação a lista
          não desmonta, preservando a posição de scroll do usuário. */}
      {loading && !data ? <LoadingSpinner /> : (
        <>
          {/* Aviso de conciliação finalizada (chamado #43) — edição bloqueada até desfazer. */}
          {!readOnly && finalizada && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, padding: '12px 16px',
              background: '#fef3c7', border: `1px solid ${colors.warning}`, borderRadius: 10, color: colors.warningDark,
            }}>
              <CheckCircle2 size={18} />
              <span style={{ fontSize: 13, fontWeight: 600 }}>
                Conciliação finalizada — os lançamentos estão bloqueados para edição. Clique em <strong>Desfazer Conciliação</strong> para editar novamente.
              </span>
            </div>
          )}

          {/* Chamado #71: lançamento que a TechMiles mandou DEPOIS de a
              conciliação ter sido finalizada. Ele é excluído da pendência de
              propósito — senão a fatura fechada se des-concilia sozinha —, mas
              antes sumia calado: a TechMiles tinha lançamento e aqui aparecia
              "nenhum lançamento" pro cartão. Agora avisa. */}
          {posFechamento > 0 && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, padding: '12px 16px',
              background: `${colors.danger}14`, border: `1px solid ${colors.danger}66`, borderRadius: 10,
            }}>
              <AlertTriangle size={18} color={colors.danger} style={{ flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: colors.text, lineHeight: 1.5 }}>
                <strong style={{ color: colors.danger }}>
                  {posFechamento} lançamento(s) da TechMiles chegaram depois do fechamento
                </strong>{' '}
                deste cartão/mês e por isso ficam fora da lista. Para tratá-los, clique em{' '}
                <strong>Desfazer Conciliação</strong>.
              </span>
            </div>
          )}

          {/* Área de match (sugestões + sem-match) — escondida no modo leitura (investidor)
              e quando a conciliação está finalizada (#43): só a lista de Conciliados/Resolvidos. */}
          {editavel && (<>
          {/* 1. Side-by-side SUGERIDOS */}
          {sugestoes.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ fontSize: 15, fontWeight: 600, color: colors.warning, marginBottom: 12 }}>
                Possíveis Matches — Aceitar ou Recusar ({sugestoes.length})
              </h3>
              <div style={{
                display: 'grid', gridTemplateColumns: '1fr auto 1fr auto', gap: 0,
                background: colors.card, borderRadius: 12, border: `1px solid ${colors.warning}44`,
                maxHeight: 500, overflowY: 'auto',
              }}>
                {/* Header */}
                <div style={{ padding: '8px 14px', borderBottom: `1px solid ${colors.border}`, fontSize: 12, fontWeight: 600, color: colors.warning, textTransform: 'uppercase' }}>
                  Fatura PDF
                </div>
                <div style={{ borderBottom: `1px solid ${colors.border}` }} />
                <div style={{ padding: '8px 14px', borderBottom: `1px solid ${colors.border}`, fontSize: 12, fontWeight: 600, color: colors.info, textTransform: 'uppercase' }}>
                  XMiles Manager
                </div>
                <div style={{ padding: '8px 14px', borderBottom: `1px solid ${colors.border}`, fontSize: 12, fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', textAlign: 'center' }}>
                  Ações
                </div>

                {sugestoes.map(c => (
                  <React.Fragment key={c.id}>
                    {/* Fatura side */}
                    <div style={{ padding: '10px 14px', borderBottom: `1px solid ${colors.border}22` }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{ flex: 1, fontSize: 13, fontWeight: 500, color: colors.text,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {c.descricao_fatura || '-'}
                          {c.fatura_parcela && (
                            <span style={{ fontSize: 11, color: colors.textMuted, marginLeft: 6 }}>
                              [{c.fatura_parcela}]
                            </span>
                          )}
                        </div>
                        {c.cartao_id && (
                          <button onClick={(e) => { e.stopPropagation(); verPdf(c.cartao_id); }}
                            title="Ver fatura PDF"
                            style={{ background: `${colors.info}22`, border: `1px solid ${colors.info}44`,
                              borderRadius: 6, color: colors.info, cursor: 'pointer',
                              padding: '2px 6px', fontSize: 10, whiteSpace: 'nowrap',
                              display: 'flex', alignItems: 'center', gap: 3 }}>
                            <FileText size={10} /> PDF
                          </button>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: colors.textDim, marginTop: 2, display: 'flex', gap: 6, alignItems: 'center' }}>
                        <span>{c.fatura_data_compra || '-'}</span>
                        {c.fatura_nome_cartao && (
                          <span>· {c.fatura_nome_cartao}{c.proprietario_cartao ? ` (${c.proprietario_cartao})` : ''}</span>
                        )}
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: colors.warning, marginTop: 2 }}>
                        {fmt(c.valor_fatura)}
                      </div>
                    </div>
                    {/* Confidence + arrow */}
                    <div style={{
                      padding: '10px 8px', borderBottom: `1px solid ${colors.border}22`,
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2,
                    }}>
                      <div style={{
                        padding: '2px 8px', borderRadius: 6,
                        background: `${confColor(c.confianca)}22`, color: confColor(c.confianca),
                        fontWeight: 700, fontSize: 12,
                      }}>
                        {c.confianca}%
                      </div>
                      {c.diferenca ? (
                        <div style={{ fontSize: 10, color: colors.danger }}>Δ {fmt(c.diferenca)}</div>
                      ) : null}
                    </div>
                    {/* XMiles side */}
                    <div style={{ padding: '10px 14px', borderBottom: `1px solid ${colors.border}22` }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{ flex: 1, fontSize: 13, fontWeight: 500, color: colors.text,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {c.descricao_xmiles || '-'}
                          {c.xmiles_parcela && (
                            <span style={{ fontSize: 11, color: colors.textMuted, marginLeft: 6 }}>
                              [{c.xmiles_parcela}]
                            </span>
                          )}
                        </div>
                        {c.transacao_xmiles_id && (
                          <button onClick={(e) => { e.stopPropagation(); abrirDetalheXmiles(c.transacao_xmiles_id); }}
                            title="Ver detalhes XMiles"
                            style={{ background: `${colors.warning}22`, border: `1px solid ${colors.warning}44`,
                              borderRadius: 6, color: colors.warning, cursor: 'pointer',
                              padding: '2px 6px', fontSize: 10, whiteSpace: 'nowrap',
                              display: 'flex', alignItems: 'center', gap: 3 }}>
                            <Eye size={10} /> Detalhes
                          </button>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: colors.textDim, marginTop: 2, display: 'flex', gap: 6, alignItems: 'center' }}>
                        <span>{c.xmiles_data_compra || '-'}</span>
                        {c.xmiles_nome_cartao && (
                          <span>· {c.xmiles_nome_cartao}{c.proprietario_cartao ? ` (${c.proprietario_cartao})` : ''}</span>
                        )}
                        {c.xmiles_origem && (
                          <Badge text={c.xmiles_origem} color={c.xmiles_origem === 'XMILES_PARC' ? colors.primary : colors.textMuted} />
                        )}
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: colors.info, marginTop: 2 }}>
                        {fmt(c.valor_xmiles)}
                      </div>
                    </div>
                    {/* Actions */}
                    <div style={{
                      padding: '10px 8px', borderBottom: `1px solid ${colors.border}22`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                    }}>
                      <button onClick={() => confirmar(c.id)} title="Aceitar match"
                        style={{ ...actionBtn(colors.success), padding: '6px 10px' }}>
                        <Check size={14} />
                      </button>
                      <button onClick={() => rejeitar(c.id)} title="Recusar (volta para lista)"
                        style={{ ...actionBtn(colors.danger), padding: '6px 10px' }}>
                        <X size={14} />
                      </button>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}

          {/* 2. Side-by-side NÃO SUGERIDOS — match manual */}
          {/* Headers */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <h3 style={{ fontSize: 15, fontWeight: 600, color: colors.textMuted, margin: 0 }}>
                Fatura PDF — Sem match ({faturaUnmatched.length})
              </h3>
              {!readOnly && faturaUnmatched.filter(t => t.valor < 0 && isPagamento(t.descricao)).length > 0 && (
                <button onClick={marcarPgtoEstornoBulk}
                  title="Resolver todos os pagamentos de fatura de uma vez (não inclui estornos)"
                  style={{
                    background: `${colors.info}22`, border: `1px solid ${colors.info}44`,
                    borderRadius: 8, color: colors.info, cursor: 'pointer',
                    padding: '3px 8px', fontSize: 10, fontWeight: 600, whiteSpace: 'nowrap',
                    display: 'flex', alignItems: 'center', gap: 4,
                  }}>
                  <ArrowDownCircle size={11} /> Resolver {faturaUnmatched.filter(t => t.valor < 0 && isPagamento(t.descricao)).length} pagamentos
                </button>
              )}
              {/* Ação em lote da seleção múltipla (chamado #22) */}
              {selPessoal.size > 0 && (
                <>
                  <button onClick={marcarPessoalLote} disabled={pessoalLote}
                    title="Marcar os selecionados como Gasto Pessoal"
                    style={{
                      background: `${colors.primary}22`, border: `1px solid ${colors.primary}66`,
                      borderRadius: 8, color: colors.primary, cursor: 'pointer',
                      padding: '3px 8px', fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap',
                      display: 'flex', alignItems: 'center', gap: 4,
                    }}>
                    <UserX size={11} /> {pessoalLote ? 'Classificando...' : `Marcar ${selPessoal.size} como Pessoal`}
                  </button>
                  {/* Operação em lote (02/09/2026): taggeia as selecionadas com um
                      centro de fechamento sem precisar casar linha por linha. */}
                  <select value={opLote} onChange={e => setOpLote(e.target.value)}
                    title="Classificar a operação (centro de fechamento) das linhas selecionadas"
                    style={{
                      background: colors.bg, color: opLote ? OPERACAO_COR(opLote) : colors.textMuted,
                      border: `1px solid ${opLote ? OPERACAO_COR(opLote) + '66' : colors.border}`,
                      borderRadius: 8, padding: '3px 6px', fontSize: 10, fontWeight: 600,
                    }}>
                    <option value="">Operação…</option>
                    {OPERACOES.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
                  </select>
                  <button onClick={anteciparLote} disabled={anteciparBusy}
                    title="Antecipar as selecionadas: conciliar cada uma com a parcela futura correspondente no XMiles"
                    style={{
                      background: `${colors.success}22`, border: `1px solid ${colors.success}66`,
                      borderRadius: 8, color: colors.success, cursor: 'pointer',
                      padding: '3px 8px', fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap',
                      display: 'flex', alignItems: 'center', gap: 4,
                    }}>
                    <Zap size={11} /> {anteciparBusy ? 'Antecipando...' : `Antecipar ${selPessoal.size}`}
                  </button>
                  {/* Avulso em lote (03/09/2026): um espelho por linha, com
                      categoria/subcategoria/empresa escolhidas uma vez só. */}
                  <button
                    onClick={() => {
                      const linhas = (data?.fatura || []).filter(t => selPessoal.has(t.id));
                      const validas = linhas.filter(t => t.cartao_id);
                      if (!validas.length) { alert('Nenhuma linha selecionada tem cartão vinculado.'); return; }
                      setAvulsoLoteModal({ linhas: validas, ignoradas: linhas.length - validas.length });
                    }}
                    title="Lançar um avulso espelho pra cada linha selecionada (categoria/subcategoria/empresa em comum) e conciliar os pares"
                    style={{
                      background: `${colors.info}22`, border: `1px solid ${colors.info}66`,
                      borderRadius: 8, color: colors.info, cursor: 'pointer',
                      padding: '3px 8px', fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap',
                      display: 'flex', alignItems: 'center', gap: 4,
                    }}>
                    <PlusCircle size={11} /> Avulso {selPessoal.size}
                  </button>
                  {opLote && (
                    <button onClick={aplicarOperacaoLote} disabled={opLoteBusy}
                      title={`Gravar ${OPERACAO_LABEL(opLote)} nas ${selPessoal.size} linhas selecionadas`}
                      style={{
                        background: `${OPERACAO_COR(opLote)}22`, border: `1px solid ${OPERACAO_COR(opLote)}66`,
                        borderRadius: 8, color: OPERACAO_COR(opLote), cursor: 'pointer',
                        padding: '3px 8px', fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap',
                      }}>
                      {opLoteBusy ? 'Aplicando...' : `Aplicar em ${selPessoal.size}`}
                    </button>
                  )}
                  <button onClick={() => setSelPessoal(new Set())} title="Limpar seleção"
                    style={{ background: 'none', border: `1px solid ${colors.border}`, borderRadius: 8,
                      color: colors.textMuted, cursor: 'pointer', padding: '3px 8px', fontSize: 10 }}>
                    Limpar
                  </button>
                </>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <h3 style={{ fontSize: 15, fontWeight: 600, color: colors.textMuted, margin: 0, whiteSpace: 'nowrap' }}>
                XMiles Manager — Sem match ({xmilesUnmatched.length})
              </h3>

              {/* Busca textual exclusiva do painel XMiles */}
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <input
                  value={buscaXm}
                  onChange={e => setBuscaXm(e.target.value)}
                  placeholder="Filtrar por nome ou valor..."
                  style={{
                    padding: '4px 26px 4px 8px', borderRadius: 7,
                    border: `1px solid ${buscaXm ? colors.primary : colors.border}`,
                    background: colors.bg, color: colors.text, fontSize: 12,
                    width: 180, outline: 'none', boxSizing: 'border-box',
                  }}
                  onFocus={e => { e.target.style.borderColor = colors.primary; }}
                  onBlur={e => { e.target.style.borderColor = buscaXm ? colors.primary : colors.border; }}
                />
                {buscaXm ? (
                  <button onClick={() => setBuscaXm('')} style={{
                    position: 'absolute', right: 5, background: 'none', border: 'none',
                    color: colors.textMuted, cursor: 'pointer', padding: 0, lineHeight: 1,
                  }}>
                    <X size={12} />
                  </button>
                ) : (
                  <span style={{ position: 'absolute', right: 7, color: colors.textDim, pointerEvents: 'none', fontSize: 11 }}>🔍</span>
                )}
              </div>

              {cartaoId && !cartaoId.startsWith('name:') && (
                <button
                  onClick={() => setXmFiltroCartao(v => !v)}
                  title={xmFiltroCartao ? 'Exibindo só este cartão — clique para ver todos' : 'Clique para filtrar só este cartão'}
                  style={{
                    background: xmFiltroCartao ? `${colors.primary}22` : `${colors.textMuted}11`,
                    border: `1px solid ${xmFiltroCartao ? colors.primary : colors.border}`,
                    borderRadius: 8, color: xmFiltroCartao ? colors.primary : colors.textMuted,
                    cursor: 'pointer', padding: '3px 8px', fontSize: 10, fontWeight: 600,
                    whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4,
                  }}>
                  <Filter size={10} />
                  {xmFiltroCartao ? 'Só este cartão' : 'Todos os cartões'}
                </button>
              )}
              {!cartaoId && (
                <span style={{ fontSize: 10, color: colors.textDim, fontStyle: 'italic', whiteSpace: 'nowrap' }}>
                  (selecione um cartão para filtrar)
                </span>
              )}
              {/* Chamado #45: a lista já é só da fatura do mês selecionado; a data de
                  cada item é a da COMPRA original (parcelas antigas caem nesta fatura). */}
              <span style={{ flexBasis: '100%', fontSize: 10, color: colors.textDim }}>
                Lançamentos da fatura de {mes} — a data exibida é a da compra original
                (parcelas de compras antigas pertencem a esta fatura).
              </span>
            </div>
          </div>

          {/* Manual match bar — between titles and lists */}
          {(selFat || selXm) && (
            <div style={{
              background: `${colors.primary}11`, border: `1px solid ${colors.primary}44`,
              borderRadius: 12, padding: '12px 16px', marginTop: 10, marginBottom: 10,
              display: 'flex', alignItems: 'center', gap: 12,
              position: 'sticky', top: 0, zIndex: 10,
            }}>
              <Link2 size={16} style={{ color: colors.primary }} />
              <span style={{ fontSize: 14, color: colors.text }}>
                Match manual: {selFat ? `Fatura #${selFat}` : 'Selecione fatura'}
                {' '}<ArrowRight size={14} style={{ verticalAlign: 'middle' }} />{' '}
                {selXm ? `XMiles #${selXm}` : 'Selecione XMiles'}
              </span>
              <div style={{ flex: 1 }} />
              {selFat && selXm && (
                <>
                  <select value={matchEmpresa} onChange={e => setMatchEmpresa(e.target.value)}
                    title="Empresa deste conciliado (chamado #40)"
                    style={{
                      background: colors.bg, color: matchEmpresa ? colors.info : colors.textMuted,
                      border: `1px solid ${matchEmpresa ? colors.info + '66' : colors.border}`,
                      borderRadius: 8, padding: '6px 10px', fontSize: 13,
                    }}>
                    <option value="">Empresa (opcional)</option>
                    {EMPRESAS.map(e => <option key={e} value={e}>{e}</option>)}
                  </select>
                  <select value={matchOperacao} onChange={e => setMatchOperacao(e.target.value)}
                    title="Operação (centro de fechamento). Em auto, não grava nada: vale a regra do relatório, inclusive o centro padrão do cartão. Escolha uma só pra fixar à mão."
                    style={{
                      background: colors.bg, color: matchOperacao ? OPERACAO_COR(matchOperacao) : colors.textMuted,
                      border: `1px solid ${matchOperacao ? OPERACAO_COR(matchOperacao) + '66' : colors.border}`,
                      borderRadius: 8, padding: '6px 10px', fontSize: 13, fontWeight: 600,
                    }}>
                    <option value="">{centroRegraSel ? `Operação (auto) — ${OPERACAO_LABEL(centroRegraSel)}` : 'Operação (auto)'}</option>
                    {OPERACOES.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
                  </select>
                  <Button onClick={matchManual} color={colors.success} size="sm" icon={Check}>Confirmar Match</Button>
                </>
              )}
              <button onClick={() => { setSelFat(null); setSelXm(null); setMatchEmpresa(''); setMatchOperacao(''); }}
                style={{ background: 'transparent', border: 'none', color: colors.textMuted, cursor: 'pointer' }}>
                <X size={16} />
              </button>
            </div>
          )}

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 520px), 1fr))',
            gap: 16,
            marginTop: (selFat || selXm) ? 0 : 12,
          }}>
            {/* Left: Fatura PDF */}
            <div>
              <div style={{
                background: colors.card, borderRadius: 12, border: `1px solid ${colors.border}`,
                maxHeight: 500, overflowY: 'auto',
              }}>
                {faturaUnmatched.length === 0 ? (
                  <div style={{ padding: 24, textAlign: 'center', color: colors.textDim, fontSize: 14 }}>
                    Nenhuma transação de fatura sem match
                  </div>
                ) : faturaUnmatched.map(t => (
                  <div key={t.id}
                    onClick={readOnly ? undefined : () => setSelFat(selFat === t.id ? null : t.id)}
                    style={{
                      padding: '10px 14px', borderBottom: `1px solid ${colors.border}22`,
                      cursor: readOnly ? 'default' : 'pointer', transition: 'background 0.15s',
                      background: selFat === t.id ? `${colors.primary}22` : 'transparent',
                      borderLeft: selFat === t.id ? `3px solid ${colors.primary}` : '3px solid transparent',
                    }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 8 }}>
                      {!readOnly && (
                      <button
                        onClick={(e) => { e.stopPropagation(); togglePessoalSel(t.id); }}
                        title="Selecionar para classificar em lote"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginTop: 1,
                          color: selPessoal.has(t.id) ? colors.primary : colors.textDim, display: 'flex', alignItems: 'center' }}>
                        {selPessoal.has(t.id) ? <CheckSquare size={16} /> : <Square size={16} />}
                      </button>
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: colors.text,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {t.descricao}
                        </div>
                        <div style={{ fontSize: 11, color: colors.textDim, marginTop: 2, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
                          <span>{t.data_compra || '-'} · {t.nome_cartao || '-'}{t.proprietario_cartao ? ` (${t.proprietario_cartao})` : ''}</span>
                          <Badge text={ORIGEM_LABEL[t.origem] || t.origem} color={t.origem === 'FATURA_PDF' ? colors.info : t.origem === 'OPEN_FINANCE' ? colors.primary : colors.textMuted} />
                          {t.situacao_fatura && (
                            <Badge text={t.situacao_fatura === 'fechada' ? 'fatura fechada' : 'fatura em aberto'}
                              color={t.situacao_fatura === 'fechada' ? colors.success : colors.warning} />
                          )}
                          {t.par_transacao_id && (
                            <span title={`A mesma compra também está na linha do ${t.origem === 'OPEN_FINANCE' ? 'PDF' : 'Open Finance'} (#${t.par_transacao_id}) — conciliar uma cobre a outra`}
                              style={{ fontSize: 10, color: colors.textDim, whiteSpace: 'nowrap' }}>
                              <Link2 size={10} style={{ verticalAlign: 'middle' }} /> par {t.origem === 'OPEN_FINANCE' ? 'PDF' : 'OF'}
                            </span>
                          )}
                          {t.removida_na_pluggy && (
                            <Badge text="removida na Pluggy" color={colors.danger} />
                          )}
                          {/* Operação já marcada à mão (02/09/2026) — mostra pra
                              conferir o que a classificação em lote já pegou. */}
                          {t.centro_fechamento && (
                            <span title="Operação (centro de fechamento) marcada manualmente"
                              style={{
                                background: `${OPERACAO_COR(t.centro_fechamento)}22`,
                                border: `1px solid ${OPERACAO_COR(t.centro_fechamento)}55`,
                                borderRadius: 4, padding: '0 4px', fontSize: 10, fontWeight: 700,
                                color: OPERACAO_COR(t.centro_fechamento), whiteSpace: 'nowrap',
                              }}>
                              {OPERACAO_LABEL(t.centro_fechamento)}
                            </span>
                          )}
                          {t.parcela && (
                            <span style={{
                              background: `${colors.primary}22`, border: `1px solid ${colors.primary}44`,
                              borderRadius: 4, padding: '0 4px', fontSize: 10, fontWeight: 600, color: colors.primary,
                            }}>
                              [{t.parcela}]
                            </span>
                          )}
                        </div>
                        {/* Chamado #71: fidelizado também do lado da fatura. O PDF do
                            banco não traz esse nome — quando existe, veio do lançamento
                            da TechMiles conciliado com esta linha. */}
                        {(t.fidelizado || t.fidelizado_conciliado) && (
                          <div style={{ fontSize: 10, marginTop: 2, color: colors.info, fontWeight: 600 }}
                            title="Fidelizado — titular da conta de fidelidade deste lançamento">
                            👤 {t.fidelizado || t.fidelizado_conciliado}
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, marginLeft: 8 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: colors.warning, whiteSpace: 'nowrap' }}>
                          {fmt(t.valor)}
                        </div>
                        <div style={{ display: 'flex', gap: 4 }}>
                          {t.cartao_id && (
                            <button
                              onClick={(e) => { e.stopPropagation(); verPdf(t.cartao_id); }}
                              title="Ver fatura PDF"
                              style={{
                                background: `${colors.info}22`, border: `1px solid ${colors.info}44`,
                                borderRadius: 6, color: colors.info, cursor: 'pointer',
                                padding: '2px 6px', fontSize: 10, whiteSpace: 'nowrap',
                                display: 'flex', alignItems: 'center', gap: 3,
                              }}>
                              <FileText size={10} /> PDF
                            </button>
                          )}
                          {!readOnly && (<>
                          <button
                            onClick={(e) => { e.stopPropagation(); marcarGastoPessoal(t.id); }}
                            title="Marcar como Gasto Pessoal"
                            style={{
                              background: `${colors.textMuted}22`, border: `1px solid ${colors.textMuted}44`,
                              borderRadius: 6, color: colors.textMuted, cursor: 'pointer',
                              padding: '2px 6px', fontSize: 10, whiteSpace: 'nowrap',
                              display: 'flex', alignItems: 'center', gap: 3,
                            }}>
                            <UserX size={10} /> Pessoal
                          </button>
                          {t.cartao_id && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setLancamentoModal({
                                cartao_id: t.cartao_id, mes_referencia: t.mes_referencia || mes, nome_cartao: t.nome_cartao,
                                // chamado #42: já traz o valor (e descrição) do lançamento da fatura
                                valor: Math.abs(parseFloat(t.valor) || 0) || '', descricao: t.descricao || '' }); }}
                              title="Lançar avulso neste cartão (entra na lista XMiles pra conciliar)"
                              style={{
                                background: `${colors.primary}22`, border: `1px solid ${colors.primary}44`,
                                borderRadius: 6, color: colors.primary, cursor: 'pointer',
                                padding: '2px 6px', fontSize: 10, whiteSpace: 'nowrap',
                                display: 'flex', alignItems: 'center', gap: 3,
                              }}>
                              <PlusCircle size={10} /> Avulso
                            </button>
                          )}
                          {/* Antecipação (02/09/2026): parcela futura cobrada nesta
                              fatura — concilia com a parcela dos meses seguintes. */}
                          {t.parcela && (
                            <button
                              onClick={(e) => { e.stopPropagation(); anteciparParcela(t.id); }}
                              title="Antecipar: conciliar com a parcela futura correspondente no XMiles (mesma compra, mesmo número de parcela) e dar baixa"
                              style={{
                                background: `${colors.success}22`, border: `1px solid ${colors.success}44`,
                                borderRadius: 6, color: colors.success, cursor: 'pointer',
                                padding: '2px 6px', fontSize: 10, whiteSpace: 'nowrap',
                                display: 'flex', alignItems: 'center', gap: 3,
                              }}>
                              <Zap size={10} /> Antecipar
                            </button>
                          )}
                          {t.valor < 0 && isPagamento(t.descricao) && (
                            <button
                              onClick={(e) => { e.stopPropagation(); marcarPgtoEstorno(t.id); }}
                              title="Marcar como Pagamento de fatura (sem correspondência no Manager)"
                              style={{
                                background: `${colors.info}22`, border: `1px solid ${colors.info}44`,
                                borderRadius: 6, color: colors.info, cursor: 'pointer',
                                padding: '2px 6px', fontSize: 10, whiteSpace: 'nowrap',
                                display: 'flex', alignItems: 'center', gap: 3,
                              }}>
                              <ArrowDownCircle size={10} /> Pgto
                            </button>
                          )}
                          </>)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: XMiles */}
            <div>
              <div style={{
                background: colors.card, borderRadius: 12, border: `1px solid ${colors.border}`,
                maxHeight: 500, overflowY: 'auto',
              }}>
                {xmilesUnmatched.length === 0 ? (
                  <div style={{ padding: 24, textAlign: 'center', color: colors.textDim, fontSize: 14 }}>
                    Nenhuma transação XMiles sem match
                  </div>
                ) : xmilesUnmatched.map(t => (
                  <div key={t.id}
                    onClick={readOnly ? undefined : () => setSelXm(selXm === t.id ? null : t.id)}
                    style={{
                      padding: '10px 14px', borderBottom: `1px solid ${colors.border}22`,
                      cursor: readOnly ? 'default' : 'pointer', transition: 'background 0.15s',
                      background: selXm === t.id ? `${colors.primary}22` : 'transparent',
                      boxShadow: selXm === t.id ? `inset 0 0 0 1px ${colors.primary}66` : 'none',
                    }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', alignItems: 'start', gap: 12 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <div title={tituloTransacaoXmiles(t)} style={{ fontSize: 13, fontWeight: 500, color: colors.text,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                            {tituloTransacaoXmiles(t)}
                          </div>
                          {t.editado === 1 && (
                            <span style={{
                              fontSize: 9, padding: '1px 5px', borderRadius: 4, fontWeight: 700,
                              background: `${colors.warning}22`, color: colors.warning,
                              border: `1px solid ${colors.warning}44`, whiteSpace: 'nowrap', flexShrink: 0,
                            }} title={`Editado${t.editado_por ? ' por ' + t.editado_por : ''}${t.editado_at ? ' em ' + new Date(t.editado_at).toLocaleString('pt-BR') : ''}`}>
                              EDITADO
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 11, color: colors.textDim, marginTop: 2, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
                          <span title="Data da compra original — parcelas antigas pertencem à fatura do mês selecionado">
                            compra {t.data_compra || '-'} · {t.nome_cartao || '-'}
                            {t.proprietario_cartao ? ` (${t.proprietario_cartao})` : ''}
                          </span>
                          <Badge text={t.origem || 'XMILES'}
                            color={t.origem === 'XMILES_PARC' ? '#8b5cf6' : t.origem === 'XMILES' ? colors.info : colors.textMuted} />
                          {t.parcela && (
                            <span style={{
                              background: `${colors.primary}22`, border: `1px solid ${colors.primary}44`,
                              borderRadius: 4, padding: '0 4px', fontSize: 10, fontWeight: 600, color: colors.primary,
                            }}>
                              [{t.parcela}]
                            </span>
                          )}
                        </div>
                        {t.categoria && (
                          <div style={{ fontSize: 10, color: colors.textMuted, marginTop: 2, fontStyle: 'italic' }}>
                            {t.categoria}
                          </div>
                        )}
                        {/* Chamado #60: fidelizado (titular da conta de fidelidade)
                            como dado próprio — identifica de quem é o clube. */}
                        {t.fidelizado && (
                          <div style={{ fontSize: 10, marginTop: 2, color: colors.info, fontWeight: 600 }}
                            title="Fidelizado — titular da conta de fidelidade deste lançamento">
                            👤 {t.fidelizado}
                          </div>
                        )}
                        {/* Chamado #47: vínculo de rastreabilidade do gasto */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }} onClick={e => e.stopPropagation()}>
                          {t.tipo_vinculo ? (
                            <span title={t.estoque_ref ? `Estoque: ${t.estoque_ref}` : t.emissao_ref ? `Emissão: ${t.emissao_ref}` : 'Classificação direta'}
                              style={{
                                fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4, whiteSpace: 'nowrap',
                                background: `${VINCULO_COR(t.tipo_vinculo)}22`, color: VINCULO_COR(t.tipo_vinculo),
                                border: `1px solid ${VINCULO_COR(t.tipo_vinculo)}55`,
                              }}>
                              🔗 {VINCULO_LABEL(t.tipo_vinculo)}{(t.estoque_ref || t.emissao_ref) ? ` · ${t.estoque_ref || t.emissao_ref}` : ''}
                            </span>
                          ) : (
                            <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4, whiteSpace: 'nowrap',
                              background: `${colors.danger}18`, color: colors.danger, border: `1px solid ${colors.danger}44` }}>
                              ⚠ sem vínculo
                            </span>
                          )}
                          {editavel && (
                            <select value={t.tipo_vinculo || ''} onChange={e => setVinculo(t.id, e.target.value, t.estoque_ref || t.emissao_ref)}
                              title="Vincular este gasto (rastreabilidade — chamado #47)"
                              style={{
                                background: colors.bg, color: colors.textMuted, border: `1px solid ${colors.border}`,
                                borderRadius: 6, padding: '1px 4px', fontSize: 10, cursor: 'pointer',
                              }}>
                              <option value="">— vincular…</option>
                              <option value="ESTOQUE">Estoque (pontos/milhas)</option>
                              <option value="EMISSAO">Emissão (taxa)</option>
                              <option value="PESSOAL">Pessoal</option>
                              <option value="ENCARGO">Encargo do cartão</option>
                              <option value="OUTRO">Outro</option>
                            </select>
                          )}
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, minWidth: 116 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: colors.info, whiteSpace: 'nowrap' }}>
                          {fmt(t.valor)}
                        </div>
                        {Number(t.total_parcelas || 1) > 1 && t.valor_total_compra != null && (
                          <div style={{ fontSize: 9, color: colors.textDim, whiteSpace: 'nowrap' }}
                            title="Valor total da compra; não entra na soma deste ciclo">
                            total {fmt(t.valor_total_compra)}
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button
                            onClick={(e) => { e.stopPropagation(); abrirDetalheXmiles(t.id); }}
                            title="Ver detalhes completos"
                            style={{
                              background: `${colors.warning}22`, border: `1px solid ${colors.warning}44`,
                              borderRadius: 6, color: colors.warning, cursor: 'pointer',
                              minHeight: 32, padding: '5px 8px', fontSize: 10, whiteSpace: 'nowrap',
                              display: 'flex', alignItems: 'center', gap: 3,
                            }}>
                            <Eye size={10} /> Detalhes
                          </button>
                          {!readOnly && (
                          <button
                            onClick={(e) => { e.stopPropagation(); setEditModal(t); }}
                            title="Editar cartão deste lançamento"
                            style={{
                              background: `${colors.primary}22`, border: `1px solid ${colors.primary}44`,
                              borderRadius: 6, color: colors.primary, cursor: 'pointer',
                              minHeight: 32, padding: '5px 8px', fontSize: 10, whiteSpace: 'nowrap',
                              display: 'flex', alignItems: 'center', gap: 3,
                            }}>
                            <Pencil size={10} /> Editar
                          </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          </>)}

          {/* 3. Lista de conciliados */}
          {(confirmados.length > 0 || buscaConc) && (
            <div style={{ marginTop: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
                <h3 style={{ fontSize: 15, fontWeight: 600, color: colors.success, margin: 0 }}>
                  Conciliados / Resolvidos ({confirmados.length})
                </h3>
                <input
                  type="text"
                  placeholder="Filtrar conciliados... (titular, cartão, descrição)"
                  value={buscaConc}
                  onChange={e => setBuscaConc(e.target.value)}
                  style={{
                    padding: '6px 12px', borderRadius: 8, border: `1px solid ${colors.border}`,
                    background: colors.bg, color: colors.text, fontSize: 13, width: 320,
                  }}
                />
              </div>

              {/* Totais + filtro pessoal vs conciliado (chamado #3) + exportar (chamado #8) */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
                {[
                  { v: 'todos', label: 'Todos', cor: colors.text, total: totaisTipo.totalPessoal + totaisTipo.totalConciliado, qtd: totaisTipo.qtdPessoal + totaisTipo.qtdConciliado },
                  { v: 'pessoal', label: 'Pessoal', cor: colors.textMuted, total: totaisTipo.totalPessoal, qtd: totaisTipo.qtdPessoal },
                  { v: 'conciliado', label: 'Conciliado', cor: colors.success, total: totaisTipo.totalConciliado, qtd: totaisTipo.qtdConciliado },
                  // Filtros por empresa (chamado #31)
                  ...EMPRESAS.map(e => ({ v: e, label: e, cor: colors.info, total: totaisTipo.porEmpresa?.[e]?.total || 0, qtd: totaisTipo.porEmpresa?.[e]?.qtd || 0 })),
                ].map(o => {
                  const ativo = filtroTipoConc === o.v;
                  return (
                    <button key={o.v} onClick={() => setFiltroTipoConc(o.v)}
                      style={{
                        cursor: 'pointer', borderRadius: 10, padding: '8px 14px', textAlign: 'left',
                        background: ativo ? `${o.cor === colors.text ? colors.primary : o.cor}18` : colors.card,
                        border: `1px solid ${ativo ? (o.cor === colors.text ? colors.primary : o.cor) : colors.border}`,
                        minWidth: o.total != null ? 150 : 80,
                      }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: ativo ? (o.cor === colors.text ? colors.primary : o.cor) : colors.textMuted }}>
                        {o.label}{o.qtd != null ? ` (${o.qtd})` : ''}
                      </div>
                      {o.total != null && (
                        <div style={{ fontSize: 16, fontWeight: 700, color: o.cor, marginTop: 2 }}>{fmt(o.total)}</div>
                      )}
                    </button>
                  );
                })}
                {/* Filtro pela coluna Operação (02/09/2026) — soma com os chips.
                    'AUTO'/'MANUAL' servem pra varrer o que falta marcar à mão. */}
                <select value={filtroOperacao} onChange={e => setFiltroOperacao(e.target.value)}
                  title="Filtrar pela coluna Operação (centro de fechamento). Combina com os chips de status/empresa. O Excel segue só o filtro dos chips."
                  style={{
                    background: filtroOperacao ? `${colors.primary}18` : colors.card,
                    color: filtroOperacao ? colors.primary : colors.textMuted,
                    border: `1px solid ${filtroOperacao ? colors.primary : colors.border}`,
                    borderRadius: 10, padding: '8px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  }}>
                  <option value="">Operação: todas</option>
                  <option value="AUTO">Só automáticas ({(totaisTipo.qtdTotalConf || 0) - (totaisTipo.qtdManuais || 0)})</option>
                  <option value="MANUAL">Só marcadas à mão ({totaisTipo.qtdManuais || 0})</option>
                  {['PESSOAL', ...OPERACOES.map(o => o.v), 'EXCLUIDO'].map(v => (
                    <option key={v} value={v}>
                      {OPERACAO_LABEL(v)} ({totaisTipo.porOperacao?.[v]?.qtd || 0})
                    </option>
                  ))}
                </select>
                {filtroOperacao && (
                  <span title="Soma das linhas visíveis com o filtro de operação aplicado"
                    style={{ fontSize: 12, fontWeight: 700, color: colors.primary, whiteSpace: 'nowrap' }}>
                    Σ {fmt(confirmados.reduce((s, c) => s + (parseFloat(c.valor_fatura) || 0), 0))}
                  </span>
                )}
                <div style={{ flex: 1 }} />
                {pdfTotalFatura != null && (
                  <div style={{
                    textAlign: 'right', marginRight: 4, padding: '6px 12px', borderRadius: 10,
                    background: `${colors.info}12`, border: `1px solid ${colors.info}40`,
                  }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: colors.textMuted }}>Total do PDF da fatura</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: colors.info }}>{fmt(pdfTotalFatura)}</div>
                  </div>
                )}
                <button onClick={exportarExcel}
                  title="Exporta os conciliados/resolvidos do filtro atual em Excel (.xlsx)"
                  style={{
                    cursor: 'pointer', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600,
                    background: colors.success, color: '#fff', border: 'none', whiteSpace: 'nowrap',
                  }}>
                  ⬇ Extrair Excel
                </button>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                      <th style={thS}>Status</th>
                      <th style={thS}>Confiança</th>
                      <th style={thS}>Titular</th>
                      <th style={thS}>Cartão</th>
                      <th style={thS}>Compra</th>
                      <th style={thS}>Empresa</th>
                      <th style={thS}>Operação</th>
                      <th style={thS}>Descrição Fatura</th>
                      <th style={{ ...thS, textAlign: 'right' }}>Valor Fatura</th>
                      <th style={thS}>Descrição XMiles</th>
                      <th style={{ ...thS, textAlign: 'right' }}>Valor XMiles</th>
                      <th style={{ ...thS, textAlign: 'right' }}>Diferença</th>
                      <th style={thS}>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {confirmados.map(c => (
                      <tr key={c.id} style={{ borderBottom: `1px solid ${colors.border}22` }}>
                        <td style={tdS}>
                          <Badge text={c.status === 'GASTO_PESSOAL' ? 'PESSOAL' : c.status === 'PGTO_ESTORNO' ? c.match_type || 'PGTO/ESTORNO' : c.match_type === 'ANTECIPACAO' ? 'ANTECIPADA' : c.status}
                            color={c.status === 'CONCILIADO' ? (c.match_type === 'ANTECIPACAO' ? colors.info : colors.success) : c.status === 'GASTO_PESSOAL' ? colors.textMuted : c.status === 'PGTO_ESTORNO' ? colors.info : colors.danger} />
                        </td>
                        <td style={tdS}>
                          {/* Chamado #46: tooltip com autoria da conciliação */}
                          <span style={{ color: confColor(c.confianca), fontWeight: 600 }}
                            title={c.conciliado_por
                              ? `Conciliado por ${c.conciliado_por}${c.conciliado_em ? ' em ' + new Date(c.conciliado_em).toLocaleString('pt-BR') : ''}`
                              : 'Sem registro de autoria (anterior ao chamado #46)'}>
                            {c.confianca}%{c.conciliado_por ? ' 👤' : ''}
                          </span>
                        </td>
                        <td style={{ ...tdS, fontSize: 12, color: colors.textMuted, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {c.fatura_titular || '-'}
                        </td>
                        <td style={{ ...tdS, fontSize: 12, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {c.fatura_nome_cartao || c.cartao || '-'}
                        </td>
                        {/* Data da compra + parcela (pedido de 02/09/2026). A API já
                            devolvia os dois lados; a fatura manda, XMiles é fallback. */}
                        <td style={{ ...tdS, fontSize: 12, whiteSpace: 'nowrap', color: colors.textMuted }}>
                          {(() => {
                            const d = c.fatura_data_compra || c.xmiles_data_compra;
                            const p = c.fatura_parcela || c.xmiles_parcela;
                            return (<>
                              <span title={d ? `Data da compra original: ${d}` : 'Sem data de compra'}>
                                {d ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(2, 4)}` : '—'}
                              </span>
                              {p && (
                                <span title="Parcela" style={{
                                  background: `${colors.primary}22`, border: `1px solid ${colors.primary}44`,
                                  borderRadius: 4, padding: '0 4px', marginLeft: 5,
                                  fontSize: 10, fontWeight: 600, color: colors.primary,
                                }}>
                                  [{p}]
                                </span>
                              )}
                            </>);
                          })()}
                        </td>
                        <td style={tdS}>
                          {c.status === 'CONCILIADO' ? (
                            // Chamado #55: conciliação rateada mostra a divisão (clicável p/ editar).
                            c.rateio ? (
                              <span
                                onClick={editavel ? () => setRateioModal(c) : undefined}
                                title={(c.rateios || []).map(r => `${r.empresa}: ${fmt(r.valor)}`).join('  ·  ')}
                                style={{
                                  fontSize: 11, fontWeight: 600, color: '#8b5cf6', cursor: editavel ? 'pointer' : 'default',
                                  display: 'inline-flex', alignItems: 'center', gap: 4,
                                }}>
                                ⚖ Rateio ({(c.rateios || []).length})
                              </span>
                            ) : !editavel ? (
                              <span style={{ color: c.empresa ? colors.info : colors.textDim, fontSize: 12 }}>{c.empresa || '—'}</span>
                            ) : (
                            <select value={c.empresa || ''} onChange={e => setEmpresaConc(c.id, e.target.value)}
                              title="Vincular este conciliado a uma empresa (ou ratear entre várias)"
                              style={{
                                background: colors.bg, color: c.empresa ? colors.info : colors.textDim,
                                border: `1px solid ${c.empresa ? colors.info + '66' : colors.border}`,
                                borderRadius: 6, padding: '3px 6px', fontSize: 12, cursor: 'pointer', maxWidth: 130,
                              }}>
                              <option value="">— sem empresa</option>
                              {EMPRESAS.map(e => <option key={e} value={e}>{e}</option>)}
                              <option value="RATEIO">⚖ Rateio…</option>
                            </select>
                            )
                          ) : <span style={{ color: colors.textDim }}>—</span>}
                        </td>
                        {/* Operação (02/09/2026): centro de fechamento da linha da
                            fatura. "Auto — X" = ainda pela regra do relatório;
                            escolher grava a marca manual (ganha da regra); voltar
                            pra Auto limpa. Linha PESSOAL ganha seletor restrito a
                            Pessoal X/J: atribui o gasto pessoal a uma operação
                            SEM tirar a marca da Rayssa (tipo_vinculo fica; a
                            linha segue contando como gasto pessoal — só o centro
                            do fechamento muda). Pgto mostra só o efetivo. */}
                        <td style={tdS}>
                          {c.status === 'GASTO_PESSOAL' && c.transacao_fatura_id && editavel ? (
                            <select value={c.fatura_centro_manual || ''}
                              onChange={e => setOperacaoConc(c.id, e.target.value)}
                              title="Atribuir este gasto pessoal a uma operação (Pessoal X/J) no relatório de fechamento — a marca de gasto pessoal continua"
                              style={{
                                background: colors.bg,
                                color: c.fatura_centro_manual ? OPERACAO_COR(c.fatura_centro_manual) : colors.textDim,
                                border: `1px solid ${c.fatura_centro_manual ? OPERACAO_COR(c.fatura_centro_manual) + '66' : colors.border}`,
                                borderRadius: 6, padding: '3px 6px', fontSize: 12, cursor: 'pointer', maxWidth: 150,
                              }}>
                              <option value="">Auto — Pessoal</option>
                              <option value="PESSOAL_X">Pessoal X</option>
                              <option value="PESSOAL_J">Pessoal J</option>
                              {c.fatura_centro_manual && !['PESSOAL_X', 'PESSOAL_J'].includes(c.fatura_centro_manual) && (
                                <option value={c.fatura_centro_manual}>{OPERACAO_LABEL(c.fatura_centro_manual)}</option>
                              )}
                            </select>
                          ) : c.status === 'CONCILIADO' && c.transacao_fatura_id ? (
                            !editavel ? (
                              <span style={{ fontSize: 11, fontWeight: 600,
                                color: c.fatura_centro_manual ? OPERACAO_COR(c.fatura_centro_manual) : colors.textDim }}>
                                {OPERACAO_LABEL(c.fatura_centro_efetivo) || '—'}
                                {!c.fatura_centro_manual && c.fatura_centro_efetivo ? ' (auto)' : ''}
                              </span>
                            ) : (
                              <select value={c.fatura_centro_manual || ''}
                                onChange={e => setOperacaoConc(c.id, e.target.value)}
                                title="Operação (centro de fechamento) desta linha no relatório Consulta → Fechamento"
                                style={{
                                  background: colors.bg,
                                  color: c.fatura_centro_manual ? OPERACAO_COR(c.fatura_centro_manual) : colors.textDim,
                                  border: `1px solid ${c.fatura_centro_manual ? OPERACAO_COR(c.fatura_centro_manual) + '66' : colors.border}`,
                                  borderRadius: 6, padding: '3px 6px', fontSize: 12, cursor: 'pointer', maxWidth: 150,
                                }}>
                                <option value="">
                                  {c.fatura_centro_efetivo ? `Auto — ${OPERACAO_LABEL(c.fatura_centro_efetivo)}` : 'Auto (regra)'}
                                </option>
                                {OPERACOES.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
                                {/* Centro gravado que saiu da lista (cadastrado e depois
                                    desativado): sem esta opção o select mostraria "Auto"
                                    e esconderia o que a linha tem de verdade. */}
                                {c.fatura_centro_manual && !OPERACOES.some(o => o.v === c.fatura_centro_manual) && (
                                  <option value={c.fatura_centro_manual}>{OPERACAO_LABEL(c.fatura_centro_manual)}</option>
                                )}
                              </select>
                            )
                          ) : (
                            <span style={{ fontSize: 11, color: colors.textDim }}
                              title="Centro efetivo no relatório de fechamento (definido pelo fluxo Pessoal/Pgto)">
                              {OPERACAO_LABEL(c.fatura_centro_efetivo) || '—'}
                            </span>
                          )}
                        </td>
                        <td style={{ ...tdS, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {c.descricao_fatura || c.descricao || '-'}
                        </td>
                        <td style={{ ...tdS, textAlign: 'right', fontWeight: 500 }}>{fmt(c.valor_fatura)}</td>
                        <td style={{ ...tdS, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {c.descricao_xmiles || '-'}
                        </td>
                        <td style={{ ...tdS, textAlign: 'right', fontWeight: 500 }}>{fmt(c.valor_xmiles)}</td>
                        <td style={{ ...tdS, textAlign: 'right', color: c.diferenca ? colors.danger : colors.textDim }}>
                          {c.diferenca ? fmt(c.diferenca) : '-'}
                        </td>
                        <td style={{ ...tdS, whiteSpace: 'nowrap' }}>
                          {c.cartao_id && (
                            <button onClick={() => verPdf(c.cartao_id)} title="Ver fatura PDF"
                              style={actionBtn(colors.info)}><FileText size={14} /></button>
                          )}
                          {c.transacao_xmiles_id && (
                            <button onClick={() => abrirDetalheXmiles(c.transacao_xmiles_id)} title="Detalhes XMiles"
                              style={actionBtn(colors.warning)}><Eye size={14} /></button>
                          )}
                          {editavel && c.status === 'CONCILIADO' && (
                            <button onClick={() => desfazer(c.id)} title="Desfazer"
                              style={actionBtn(colors.warning)}><Unlink size={14} /></button>
                          )}
                          {editavel && c.status === 'GASTO_PESSOAL' && (
                            <button onClick={() => desfazerPessoal(c.transacao_fatura_id)} title="Desfazer Gasto Pessoal"
                              style={actionBtn(colors.warning)}><Undo2 size={14} /></button>
                          )}
                          {editavel && c.status === 'PGTO_ESTORNO' && (
                            <button onClick={() => desfazerPessoal(c.transacao_fatura_id)} title="Desfazer Pgto/Estorno"
                              style={actionBtn(colors.warning)}><Undo2 size={14} /></button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Inline styles for the conciliation table
const thS = { padding: '8px 10px', textAlign: 'left', color: colors.textMuted, fontWeight: 500, fontSize: 12, textTransform: 'uppercase' };
const tdS = { padding: '8px 10px', color: colors.text };
const actionBtn = (color) => ({
  background: `${color}22`, border: `1px solid ${color}44`, borderRadius: 6,
  color, cursor: 'pointer', padding: '4px 6px', marginRight: 4,
});

function EditarCartaoModal({ transacao, cartoes, onClose, onSave, onSaveParcela, onParcelar }) {
  const [aba, setAba] = useState('parcela'); // 'parcela' | 'cartao'

  // --- Aba Parcela ---
  const [novoValor, setNovoValor] = useState(String(transacao.valor || ''));
  const [novaData, setNovaData]   = useState(transacao.data_vencimento || '');
  const [motivoParc, setMotivoParc] = useState('');
  const [salvandoParc, setSalvandoParc] = useState(false);
  // Parcelamento (17/09/2026): a compra veio do TechMiles pelo valor cheio e no
  // cartão foi parcelada. O valor/data acima valem como base da divisão.
  const [parcelar, setParcelar] = useState(false);
  const [qtdParcelas, setQtdParcelas] = useState('2');

  // --- Aba Cartão ---
  const [novoCartaoId, setNovoCartaoId] = useState(transacao.cartao_id ? String(transacao.cartao_id) : '');
  const [motivoCartao, setMotivoCartao] = useState('');
  const [salvandoCartao, setSalvandoCartao] = useState(false);

  // --- Histórico ---
  const [auditoria, setAuditoria] = useState(null);
  useEffect(() => {
    if (transacao.editado === 1) {
      fetch(`${API}/transacoes/${transacao.id}/auditoria`)
        .then(r => r.json()).then(d => setAuditoria(Array.isArray(d) ? d : [])).catch(() => {});
    }
  }, [transacao.id, transacao.editado]);

  const salvando = salvandoParc || salvandoCartao;
  const cartaoAtual = cartoes.find(c => c.id === transacao.cartao_id);
  // Parcelado/conciliado não se parcela — a rota recusa, e o botão nem aparece.
  const podeParcelar = transacao.origem !== 'FATURA_PDF'
    && !transacao.conciliado
    && !(transacao.total_parcelas > 1);
  const diaVenc = diaDoVencimento(cartaoAtual?.data_vencimento);
  const preview = parcelar
    ? previewParcelamento({ valorTotal: novoValor, dataBase: novaData, quantidade: qtdParcelas, diaVenc })
    : null;

  const handleParcelar = async () => {
    if (!motivoParc.trim()) { alert('Informe o motivo do parcelamento'); return; }
    if (!preview) { alert('Confira a quantidade de parcelas, o valor e a data'); return; }
    setSalvandoParc(true);
    await onParcelar(transacao.id, {
      quantidade: parseInt(qtdParcelas, 10),
      motivo: motivoParc.trim(),
      novo_valor: parseFloat(novoValor),
      nova_data: novaData,
    });
    setSalvandoParc(false);
  };

  const handleSaveParc = async () => {
    if (!motivoParc.trim()) { alert('Informe o motivo do ajuste'); return; }
    const valorMudou = parseFloat(novoValor) !== parseFloat(transacao.valor);
    const dataMudou  = novaData !== transacao.data_vencimento;
    if (!valorMudou && !dataMudou) { alert('Nenhuma alteração detectada — valor e data são os mesmos'); return; }
    setSalvandoParc(true);
    await onSaveParcela(transacao.id, {
      novo_valor: valorMudou ? parseFloat(novoValor) : undefined,
      nova_data:  dataMudou  ? novaData : undefined,
      motivo: motivoParc.trim(),
    });
    setSalvandoParc(false);
  };

  const handleSaveCartao = async () => {
    if (!novoCartaoId) { alert('Selecione o cartão correto'); return; }
    if (parseInt(novoCartaoId) === transacao.cartao_id) { alert('O cartão selecionado é o mesmo'); return; }
    if (!motivoCartao.trim()) { alert('Informe o motivo da correção'); return; }
    setSalvandoCartao(true);
    await onSave(transacao.id, novoCartaoId, motivoCartao.trim());
    setSalvandoCartao(false);
  };

  const tabStyle = (active) => ({
    flex: 1, padding: '8px 0', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: active ? 700 : 500,
    background: active ? colors.card : 'transparent',
    color: active ? colors.primary : colors.textMuted,
    borderBottom: active ? `2px solid ${colors.primary}` : `2px solid transparent`,
    transition: 'all 0.15s',
  });

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.6)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: colors.card, borderRadius: 16, padding: 28, width: 560,
        border: `1px solid ${colors.border}`, boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
        maxHeight: '90vh', overflowY: 'auto',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div style={{
            width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: `${colors.primary}22`, color: colors.primary,
          }}><Pencil size={18} /></div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: colors.text }}>Editar Lançamento XMiles</div>
            <div style={{ fontSize: 11, color: colors.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {transacao.descricao}
              {transacao.parcela && <span style={{ color: colors.primary, marginLeft: 6 }}>[{transacao.parcela}]</span>}
            </div>
          </div>
          <button onClick={onClose} disabled={salvando} style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer' }}>
            <X size={18} />
          </button>
        </div>

        {/* Info do lançamento */}
        <div style={{
          display: 'flex', gap: 10, marginBottom: 16, padding: '8px 12px',
          background: colors.bg, borderRadius: 8, fontSize: 12,
        }}>
          <span style={{ color: colors.textMuted }}>{transacao.data_vencimento || transacao.data_compra || '-'}</span>
          <span style={{ color: colors.border }}>·</span>
          <span style={{ fontWeight: 600, color: colors.info }}>
            R$ {Number(transacao.valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
          </span>
          <span style={{ color: colors.border }}>·</span>
          <span style={{ color: colors.textMuted }}>{transacao.nome_cartao || '—'}</span>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: `1px solid ${colors.border}`, marginBottom: 20 }}>
          <button style={tabStyle(aba === 'parcela')} onClick={() => setAba('parcela')}>
            ✏️ Ajustar Valor / Data
          </button>
          <button style={tabStyle(aba === 'cartao')} onClick={() => setAba('cartao')}>
            🔄 Trocar Cartão
          </button>
        </div>

        {/* ── ABA PARCELA ── */}
        {aba === 'parcela' && (
          <>
            <div style={{
              background: `${colors.info}11`, border: `1px solid ${colors.info}33`,
              borderRadius: 8, padding: '8px 12px', marginBottom: 16, fontSize: 11, color: colors.textMuted,
              display: 'flex', gap: 6, alignItems: 'flex-start',
            }}>
              <span>ℹ️</span>
              <span>Edição local: ajusta valor e data do lançamento (e da conciliação, se houver). Marque <strong>Parcelar</strong> para dividir o valor entre as próximas faturas.</span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: colors.textMuted, marginBottom: 5, textTransform: 'uppercase' }}>
                  Valor atual
                </label>
                <div style={{ fontSize: 13, padding: '8px 10px', background: `${colors.border}22`, borderRadius: 6, color: colors.textDim }}>
                  R$ {Number(transacao.valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: colors.textMuted, marginBottom: 5, textTransform: 'uppercase' }}>
                  Novo valor *
                </label>
                <input
                  type="number" step="0.01" min="0"
                  value={novoValor}
                  onChange={e => setNovoValor(e.target.value)}
                  style={{
                    width: '100%', padding: '8px 10px', borderRadius: 6, border: `1px solid ${colors.border}`,
                    background: colors.bg, color: colors.text, fontSize: 13, outline: 'none', boxSizing: 'border-box',
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: colors.textMuted, marginBottom: 5, textTransform: 'uppercase' }}>
                  Data vencimento atual
                </label>
                <div style={{ fontSize: 13, padding: '8px 10px', background: `${colors.border}22`, borderRadius: 6, color: colors.textDim }}>
                  {transacao.data_vencimento || '—'}
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: colors.textMuted, marginBottom: 5, textTransform: 'uppercase' }}>
                  Nova data vencimento *
                </label>
                <input
                  type="date"
                  value={novaData}
                  onChange={e => setNovaData(e.target.value)}
                  style={{
                    width: '100%', padding: '8px 10px', borderRadius: 6, border: `1px solid ${colors.border}`,
                    background: colors.bg, color: colors.text, fontSize: 13, outline: 'none',
                    colorScheme: 'dark', boxSizing: 'border-box',
                  }}
                />
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: colors.textMuted, marginBottom: 5, textTransform: 'uppercase' }}>
                Motivo *
              </label>
              <textarea
                value={motivoParc}
                onChange={e => setMotivoParc(e.target.value)}
                placeholder="Ex: compra parcelada em 2x no cartão, ajuste de vencimento..."
                rows={2}
                style={{
                  width: '100%', padding: '8px 10px', borderRadius: 6, border: `1px solid ${colors.border}`,
                  background: colors.bg, color: colors.text, fontSize: 13, outline: 'none',
                  resize: 'vertical', boxSizing: 'border-box',
                }}
              />
            </div>

            {/* Parcelamento: divide o valor acima em N parcelas, a 1ª ficando
                nesta fatura e as demais nas seguintes. */}
            {podeParcelar && (
              <div style={{
                border: `1px solid ${parcelar ? colors.primary : colors.border}`,
                background: parcelar ? `${colors.primary}0D` : 'transparent',
                borderRadius: 8, padding: '10px 12px', marginBottom: 16,
              }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={parcelar} onChange={e => setParcelar(e.target.checked)} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: colors.text }}>Parcelar este lançamento</span>
                  <span style={{ fontSize: 11, color: colors.textMuted }}>· divide o valor entre as faturas</span>
                </label>

                {parcelar && (
                  <div style={{ marginTop: 12 }}>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: colors.textMuted, marginBottom: 5, textTransform: 'uppercase' }}>
                      Quantidade de parcelas *
                    </label>
                    <input
                      type="number" min="2" max="60" step="1"
                      value={qtdParcelas}
                      onChange={e => setQtdParcelas(e.target.value)}
                      style={{
                        width: 110, padding: '8px 10px', borderRadius: 6, border: `1px solid ${colors.border}`,
                        background: colors.bg, color: colors.text, fontSize: 13, outline: 'none', boxSizing: 'border-box',
                      }}
                    />

                    {preview ? (
                      <div style={{ marginTop: 12, background: colors.bg, borderRadius: 8, padding: '8px 10px' }}>
                        <div style={{ fontSize: 11, color: colors.textMuted, marginBottom: 6 }}>
                          Preview ({preview.length} parcelas)
                        </div>
                        <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ color: colors.textMuted }}>
                              <th style={{ textAlign: 'left', fontWeight: 500, padding: '2px 0' }}>#</th>
                              <th style={{ textAlign: 'left', fontWeight: 500, padding: '2px 0' }}>Vencimento</th>
                              <th style={{ textAlign: 'left', fontWeight: 500, padding: '2px 0' }}>Fatura</th>
                              <th style={{ textAlign: 'right', fontWeight: 500, padding: '2px 0' }}>Valor</th>
                            </tr>
                          </thead>
                          <tbody>
                            {preview.map(p => (
                              <tr key={p.numero} style={{ color: colors.primary }}>
                                <td style={{ padding: '3px 0' }}>{p.numero}/{preview.length}</td>
                                <td style={{ padding: '3px 0' }}>{p.data.split('-').reverse().join('/')}</td>
                                <td style={{ padding: '3px 0' }}>{p.mes}</td>
                                <td style={{ padding: '3px 0', textAlign: 'right' }}>
                                  R$ {p.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                </td>
                              </tr>
                            ))}
                            <tr style={{ color: colors.text, fontWeight: 600, borderTop: `1px solid ${colors.border}` }}>
                              <td style={{ padding: '5px 0' }} colSpan={3}>Total</td>
                              <td style={{ padding: '5px 0', textAlign: 'right' }}>
                                R$ {preview.reduce((t, p) => t + p.valor, 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                        <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 6 }}>
                          A 1ª parcela fica neste lançamento; as demais nascem nas faturas seguintes,
                          pendentes de conciliação.
                        </div>
                      </div>
                    ) : (
                      <div style={{ fontSize: 11, color: colors.warning, marginTop: 8 }}>
                        Informe de 2 a 60 parcelas, com valor e data válidos.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {salvandoParc && <LoadingMsg text={parcelar ? 'Parcelando lançamento...' : 'Ajustando parcela...'} />}

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={onClose} disabled={salvando} style={btnCancelar(salvando)}>Cancelar</button>
              {parcelar ? (
                <button onClick={handleParcelar} disabled={salvando || !motivoParc.trim() || !preview}
                  style={btnPrimario(salvando || !motivoParc.trim() || !preview)}>
                  {salvandoParc ? '⏳ Processando...' : `🧾 Parcelar em ${preview ? preview.length : qtdParcelas}x`}
                </button>
              ) : (
                <button onClick={handleSaveParc} disabled={salvando || !motivoParc.trim()} style={btnPrimario(salvando || !motivoParc.trim())}>
                  {salvandoParc ? '⏳ Processando...' : '✏️ Ajustar parcela'}
                </button>
              )}
            </div>
          </>
        )}

        {/* ── ABA CARTÃO ── */}
        {aba === 'cartao' && (
          <>
            <div style={{
              background: `${colors.warning}11`, border: `1px solid ${colors.warning}33`,
              borderRadius: 8, padding: '8px 12px', marginBottom: 16, fontSize: 11, color: colors.textMuted,
              display: 'flex', gap: 6, alignItems: 'flex-start',
            }}>
              <span>🔄</span>
              <span>Cancela o lançamento no Manager e recria no cartão correto com todos os dados originais preservados (descrição, valor, parcelas, emissão vinculada).</span>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: colors.textMuted, marginBottom: 5, textTransform: 'uppercase' }}>
                Cartão atual
              </label>
              <div style={{ fontSize: 13, padding: '8px 10px', background: `${colors.danger}11`, borderRadius: 6, border: `1px solid ${colors.danger}22`, color: colors.text }}>
                {cartaoAtual ? `${cartaoAtual.nome_cartao}${cartaoAtual.titular_nome ? ' (' + cartaoAtual.titular_nome + ')' : ''}` : transacao.nome_cartao || '—'}
              </div>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: colors.textMuted, marginBottom: 5, textTransform: 'uppercase' }}>
                Cartão correto *
              </label>
              <select value={novoCartaoId} onChange={e => setNovoCartaoId(e.target.value)} style={{
                width: '100%', padding: '8px 10px', borderRadius: 6, border: `1px solid ${colors.border}`,
                background: colors.bg, color: colors.text, fontSize: 13, outline: 'none',
              }}>
                <option value="">— Selecione —</option>
                {cartoes.map(c => (
                  <option key={c.id} value={String(c.id)}>
                    {c.nome_cartao}{c.titular_nome ? ` (${c.titular_nome})` : ''}{c.numeracao_final ? ` ·${c.numeracao_final}` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: colors.textMuted, marginBottom: 5, textTransform: 'uppercase' }}>
                Motivo *
              </label>
              <textarea
                value={motivoCartao}
                onChange={e => setMotivoCartao(e.target.value)}
                placeholder="Ex: lançamento importado no cartão errado, correto é..."
                rows={2}
                style={{
                  width: '100%', padding: '8px 10px', borderRadius: 6, border: `1px solid ${colors.border}`,
                  background: colors.bg, color: colors.text, fontSize: 13, outline: 'none',
                  resize: 'vertical', boxSizing: 'border-box',
                }}
              />
            </div>

            {salvandoCartao && <LoadingMsg text="Cancelando e recriando no Manager..." />}

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={onClose} disabled={salvando} style={btnCancelar(salvando)}>Cancelar</button>
              <button onClick={handleSaveCartao} disabled={salvando || !novoCartaoId || !motivoCartao.trim()} style={btnPrimario(salvando || !novoCartaoId || !motivoCartao.trim())}>
                {salvandoCartao ? '⏳ Processando...' : '🔄 Corrigir no Manager'}
              </button>
            </div>
          </>
        )}

        {/* Histórico de edições */}
        {transacao.editado === 1 && auditoria && auditoria.length > 0 && (
          <div style={{ background: colors.bg, borderRadius: 8, padding: '10px 12px', marginTop: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', marginBottom: 8 }}>
              <History size={12} /> Histórico
            </div>
            {auditoria.map((a, i) => (
              <div key={a.id || i} style={{
                fontSize: 11, color: colors.textDim, padding: '4px 0',
                borderBottom: i < auditoria.length - 1 ? `1px solid ${colors.border}22` : 'none',
              }}>
                <span style={{ color: colors.warning, fontWeight: 600 }}>{a.campo}</span>
                {': '}
                <span style={{ color: colors.danger }}>{a.valor_anterior || '—'}</span>
                {' → '}
                <span style={{ color: colors.success }}>{a.valor_novo || '—'}</span>
                <span style={{ color: colors.textDim, marginLeft: 8 }}>
                  {a.usuario_login && `por ${a.usuario_login}`}
                  {a.created_at && ` · ${new Date(a.created_at).toLocaleString('pt-BR')}`}
                </span>
                {a.motivo && <div style={{ color: colors.textMuted, fontStyle: 'italic', marginTop: 1 }}>"{a.motivo}"</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Helpers de estilo para os modais
const btnCancelar = (disabled) => ({
  flex: 1, padding: '10px', borderRadius: 10, border: `1px solid ${colors.border}`,
  background: 'transparent', color: disabled ? colors.textDim : colors.textMuted,
  cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 14,
});
const btnPrimario = (disabled) => ({
  flex: 2, padding: '10px', borderRadius: 10, border: 'none',
  background: disabled ? `${colors.primary}55` : colors.primary,
  color: '#fff', cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600,
});
function LoadingMsg({ text }) {
  return (
    <div style={{
      background: `${colors.primary}11`, border: `1px solid ${colors.primary}33`,
      borderRadius: 8, padding: '10px 12px', marginBottom: 14,
      display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, color: colors.primary,
    }}>
      <div style={{
        width: 14, height: 14, borderRadius: '50%', flexShrink: 0,
        border: `2px solid ${colors.primary}44`, borderTopColor: colors.primary,
        animation: 'spin 0.8s linear infinite',
      }} />
      <div style={{ fontWeight: 600 }}>{text}</div>
    </div>
  );
}

// Por que o lançamento realocado sumiu da lista. A conciliação esconde cartão
// morto (#18/#36) e em negociação (#54) por padrão, e escopa pelo dono do cartão
// — então trocar o cartão pode tirar a linha da tela. Antes ela só sumia, o que
// é indistinguível de perda de dado; aqui dizemos onde ela está e como chegar lá.
// Lista vazia = o lançamento continua visível, e nenhum aviso é mostrado.
export function motivosForaDoEscopo(destino, filtro) {
  if (!destino) return [];
  const motivos = [];
  if (destino.inativo && !filtro?.incluirInativos) {
    motivos.push('o cartão de destino está inativo ou cancelado — ligue "incluir inativos" para vê-lo');
  }
  if (destino.em_negociacao && !filtro?.incluirNegociacao) {
    motivos.push('o cartão de destino está em gerenciamento de crise — ligue "incluir negociação" para vê-lo');
  }
  if (filtro?.titularId && String(destino.titular_id) !== String(filtro.titularId)) {
    motivos.push(`o cartão pertence a ${destino.titular_nome || 'outro titular'} — troque o filtro de titular para encontrá-lo`);
  }
  return motivos;
}

function RealocacaoResultModal({ data, onClose, filtro }) {
  const isParcela = data._tipo === 'parcela';
  const isEdDireto = isParcela && data.path_taken === 'EDIT_PARCELA';

  const saiuDoEscopo = isParcela ? [] : motivosForaDoEscopo(data.destino, filtro);

  const row = (label, value, color) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: `1px solid ${colors.border}22` }}>
      <span style={{ fontSize: 12, color: colors.textMuted }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 500, color: color || colors.text }}>{value || '-'}</span>
    </div>
  );

  const titulo = isParcela
    ? (isEdDireto ? 'Parcela ajustada diretamente' : 'Parcela ajustada via cancel+recriar')
    : 'Cartão corrigido no Manager';
  const subtitulo = isParcela
    ? (isEdDireto ? 'Editado diretamente via PUT /fin-parcs' : 'API rejeitou edição direta — lançamento cancelado e recriado com os novos valores')
    : 'Lançamento cancelado e recriado no cartão correto';

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.6)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: colors.card, borderRadius: 16, padding: 28, width: 500,
        border: `1px solid ${colors.success}`, boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
        maxHeight: '90vh', overflowY: 'auto',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div style={{
            width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: `${colors.success}22`, color: colors.success, fontSize: 20,
          }}>{isParcela ? '✏️' : '✓'}</div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: colors.success }}>{titulo}</div>
            <div style={{ fontSize: 11, color: colors.textMuted }}>{subtitulo}</div>
          </div>
          <div style={{ flex: 1 }} />
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer' }}>
            <X size={18} />
          </button>
        </div>

        {/* Conteúdo: parcela ajustada */}
        {isParcela && (
          <>
            {/* Método usado */}
            <div style={{
              display: 'flex', gap: 8, alignItems: 'center', marginBottom: 14, padding: '8px 12px',
              borderRadius: 8, fontSize: 12,
              background: isEdDireto ? `${colors.success}11` : `${colors.warning}11`,
              border: `1px solid ${isEdDireto ? colors.success : colors.warning}33`,
              color: isEdDireto ? colors.success : colors.warning,
            }}>
              <span>{isEdDireto ? '⚡' : '🔄'}</span>
              <span style={{ fontWeight: 600 }}>
                {isEdDireto ? 'Editado diretamente (PUT /fin-parcs)' : 'API rejeitou edição direta → cancel+recriar automático'}
              </span>
            </div>

            {/* IDs Manager só se cancel+recriar */}
            {!isEdDireto && data.manager_id_cancelado && (
              <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                <div style={{ flex: 1, padding: '8px 10px', borderRadius: 8, textAlign: 'center', background: `${colors.danger}11`, border: `1px solid ${colors.danger}33` }}>
                  <div style={{ fontSize: 10, color: colors.textMuted, textTransform: 'uppercase', marginBottom: 2 }}>Cancelado</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: colors.danger }}>#{data.manager_id_cancelado}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', color: colors.textDim }}>→</div>
                <div style={{ flex: 1, padding: '8px 10px', borderRadius: 8, textAlign: 'center', background: `${colors.success}11`, border: `1px solid ${colors.success}33` }}>
                  <div style={{ fontSize: 10, color: colors.textMuted, textTransform: 'uppercase', marginBottom: 2 }}>Novo</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: colors.success }}>#{data.manager_id_novo}</div>
                </div>
              </div>
            )}

            {/* Alterações */}
            <div style={{ background: colors.bg, borderRadius: 8, padding: '10px 12px', marginBottom: 20 }}>
              {data.valor_anterior != null && data.valor_novo != null && row(
                'Valor',
                `R$ ${Number(data.valor_anterior).toLocaleString('pt-BR', { minimumFractionDigits: 2 })} → R$ ${Number(data.valor_novo).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
                Math.abs(data.valor_novo - data.valor_anterior) > 0.001 ? colors.warning : colors.textMuted
              )}
              {data.data_anterior && data.data_nova && row(
                'Vencimento',
                `${data.data_anterior} → ${data.data_nova}`,
                data.data_anterior !== data.data_nova ? colors.info : colors.textMuted
              )}
              {isEdDireto && data.parc_id && row('Parcela Manager ID', `#${data.parc_id}`, colors.textMuted)}
            </div>
          </>
        )}

        {/* Conteúdo: cartão trocado */}
        {!isParcela && (
          <>
            <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
              <div style={{ flex: 1, padding: '10px 12px', borderRadius: 8, textAlign: 'center', background: `${colors.danger}11`, border: `1px solid ${colors.danger}33` }}>
                <div style={{ fontSize: 10, color: colors.textMuted, textTransform: 'uppercase', marginBottom: 2 }}>Cancelado</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: colors.danger }}>#{data.manager_id_cancelado}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', color: colors.textDim }}>→</div>
              <div style={{ flex: 1, padding: '10px 12px', borderRadius: 8, textAlign: 'center', background: `${colors.success}11`, border: `1px solid ${colors.success}33` }}>
                <div style={{ fontSize: 10, color: colors.textMuted, textTransform: 'uppercase', marginBottom: 2 }}>Novo</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: colors.success }}>#{data.manager_id_novo}</div>
              </div>
            </div>

            <div style={{ background: colors.bg, borderRadius: 8, padding: '10px 12px', marginBottom: 14 }}>
              {row('Descrição', data.complement)}
              {data.valor != null && row('Valor', `R$ ${Number(data.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, colors.warning)}
              {data.num_parcelas > 1 && row('Parcelas', `${data.num_parcelas}x`)}
              {data.emission_id && row('Emissão', `#${data.emission_id}`, colors.primary)}
            </div>

            <div style={{ background: colors.bg, borderRadius: 8, padding: '10px 12px', marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ flex: 1, padding: '6px 8px', borderRadius: 6, background: `${colors.danger}11`, fontSize: 12, color: colors.danger, textAlign: 'center' }}>{data.cartao_anterior}</div>
                <span style={{ color: colors.textDim }}>→</span>
                <div style={{ flex: 1, padding: '6px 8px', borderRadius: 6, background: `${colors.success}11`, fontSize: 12, color: colors.success, textAlign: 'center', fontWeight: 600 }}>{data.cartao_novo}</div>
              </div>
              {data.titular_novo && <div style={{ fontSize: 11, color: colors.textDim, marginTop: 6, textAlign: 'center' }}>Titular: {data.titular_novo}</div>}
            </div>

            {saiuDoEscopo.length > 0 && (
              <div style={{
                background: `${colors.warning}11`, border: `1px solid ${colors.warning}44`,
                borderRadius: 8, padding: '10px 12px', marginBottom: 20,
                fontSize: 12, color: colors.text,
              }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 6 }}>
                  <span>⚠️</span>
                  <span style={{ fontWeight: 600, color: colors.warning }}>
                    O lançamento saiu desta lista — ele não foi perdido
                  </span>
                </div>
                <div style={{ color: colors.textMuted, lineHeight: 1.5 }}>
                  Ele está no cartão <strong style={{ color: colors.text }}>{data.cartao_novo}</strong>
                  {data.titular_novo ? <> (titular {data.titular_novo})</> : null}, mas não aparece no filtro atual porque:
                  <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                    {saiuDoEscopo.map((m, i) => <li key={i} style={{ marginBottom: 2 }}>{m}</li>)}
                  </ul>
                </div>
              </div>
            )}
          </>
        )}

        <button onClick={onClose} style={{
          width: '100%', padding: '10px', borderRadius: 10, border: 'none',
          background: colors.success, color: '#fff', cursor: 'pointer',
          fontSize: 14, fontWeight: 600,
        }}>
          OK — Fechar
        </button>
      </div>
    </div>
  );
}

// Modal "Rateio" (chamado #55): divide o valor de um conciliado entre 2+ empresas
// (Ivoei/XMiles/Franchising). Default: divisão igual entre as marcadas; o usuário
// pode ajustar cada valor, desde que a soma bata com o total (tolerância 1 centavo).
function RateioModal({ conc, onClose, onSave, onCancelarRateio }) {
  const total = Math.round(Math.abs(parseFloat(conc.valor_fatura ?? conc.valor_xmiles) || 0) * 100) / 100;
  // Inicializa com o rateio existente, se houver; senão nenhuma empresa marcada.
  const iniciais = {};
  (conc.rateios || []).forEach(r => { iniciais[r.empresa] = String(r.valor); });
  const [sel, setSel] = useState(() => {
    const s = {}; EMPRESAS.forEach(e => { s[e] = (conc.rateios || []).some(r => r.empresa === e); }); return s;
  });
  const [valores, setValores] = useState(iniciais);
  const [saving, setSaving] = useState(false);
  const marcadas = EMPRESAS.filter(e => sel[e]);

  // Divide igualmente entre as marcadas (resto na última) e preenche os valores.
  const dividirIgual = (lista) => {
    const n = lista.length; if (!n) return {};
    const base = Math.floor((total / n) * 100) / 100;
    const v = {};
    lista.forEach((e, i) => { v[e] = (i === n - 1 ? Math.round((total - base * (n - 1)) * 100) / 100 : base).toFixed(2); });
    return v;
  };
  const toggle = (e) => {
    const novoSel = { ...sel, [e]: !sel[e] };
    setSel(novoSel);
    const lista = EMPRESAS.filter(x => novoSel[x]);
    setValores(dividirIgual(lista)); // redivide igual ao marcar/desmarcar
  };

  const soma = Math.round(marcadas.reduce((s, e) => s + (parseFloat(valores[e]) || 0), 0) * 100) / 100;
  const bate = Math.abs(soma - total) <= 0.01;
  const valido = marcadas.length >= 2 && bate;

  const inp = { width: 110, padding: '6px 8px', borderRadius: 8, border: `1px solid ${colors.border}`, background: colors.bg, color: colors.text, fontSize: 13, textAlign: 'right', boxSizing: 'border-box' };

  const salvar = async () => {
    if (!valido || saving) return;
    setSaving(true);
    try {
      await onSave(conc.id, marcadas.map(e => ({ empresa: e, valor: parseFloat(valores[e]) })));
    } catch (err) { alert('Erro ao ratear: ' + err.message); setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: colors.card, borderRadius: 16, padding: 28, width: 440, border: `1px solid ${colors.border}`, boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: colors.text }}>⚖ Ratear entre empresas</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer' }}><X size={18} /></button>
        </div>
        <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 16 }}>
          {conc.descricao_fatura || conc.descricao || 'Despesa'} · total <strong>{fmt(total)}</strong>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {EMPRESAS.map(e => (
            <div key={e} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
              padding: '8px 10px', borderRadius: 10, border: `1px solid ${sel[e] ? colors.info + '66' : colors.border}`,
              background: sel[e] ? `${colors.info}11` : 'transparent' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, color: colors.text, fontWeight: 600 }}>
                <input type="checkbox" checked={!!sel[e]} onChange={() => toggle(e)} /> {e}
              </label>
              <input style={{ ...inp, opacity: sel[e] ? 1 : 0.4 }} type="number" step="0.01" disabled={!sel[e]}
                value={sel[e] ? (valores[e] ?? '') : ''} placeholder="0,00"
                onChange={ev => setValores(v => ({ ...v, [e]: ev.target.value }))} />
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14, fontSize: 13 }}>
          <span style={{ color: colors.textMuted }}>Soma: <strong style={{ color: bate ? colors.success : colors.danger }}>{fmt(soma)}</strong> / {fmt(total)}</span>
          {marcadas.length >= 2 && (
            <button onClick={() => setValores(dividirIgual(marcadas))}
              style={{ background: 'none', border: `1px solid ${colors.border}`, borderRadius: 8, color: colors.textMuted, cursor: 'pointer', padding: '4px 10px', fontSize: 11 }}>
              Dividir igual
            </button>
          )}
        </div>
        {!bate && marcadas.length >= 2 && (
          <div style={{ fontSize: 11, color: colors.danger, marginTop: 6 }}>A soma precisa ser igual ao total ({fmt(total)}).</div>
        )}
        {marcadas.length < 2 && (
          <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 6 }}>Marque ao menos 2 empresas para ratear.</div>
        )}

        {/* Desfazer um rateio já aplicado: a linha volta pro select de empresa
            única (pedido do Murilo 02/09). Só aparece quando JÁ existe rateio. */}
        {conc.rateio && onCancelarRateio && (
          <button onClick={async () => { if (!window.confirm('Cancelar o rateio? A linha volta a ter uma única empresa (a escolher no select).')) return; setSaving(true); try { await onCancelarRateio(); } finally { setSaving(false); } }}
            disabled={saving}
            style={{ width: '100%', marginTop: 14, padding: '9px', borderRadius: 10, border: `1px solid ${colors.danger}66`, background: 'transparent', color: colors.danger, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            ✕ Cancelar rateio (voltar a empresa única)
          </button>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: conc.rateio ? 10 : 20 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '10px', borderRadius: 10, border: `1px solid ${colors.border}`, background: 'transparent', color: colors.textMuted, cursor: 'pointer', fontSize: 14 }}>Cancelar</button>
          <button onClick={salvar} disabled={!valido || saving}
            style={{ flex: 1, padding: '10px', borderRadius: 10, border: 'none', background: valido ? colors.success : colors.border, color: '#fff', cursor: valido ? 'pointer' : 'not-allowed', fontSize: 14, fontWeight: 600 }}>
            {saving ? 'Salvando...' : 'Aplicar rateio'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Modal "Fazer Lançamento" (chamado #30): cria um lançamento local (avulso ou
// recorrente) na fatura do cartão/mês selecionado. Recorrente gera 1 parcela por
// mês até completar a quantidade. Empresa (chamado #31) opcional.
// Avulso em lote (03/09/2026): pra cada linha de fatura selecionada, o server
// cria um avulso espelho (mesma descrição/valor/data/parcela) com a
// classificação escolhida aqui UMA vez e, por padrão, já concilia cada par.
// Caso de uso: parcelas antecipadas sem lançamento no XMiles.
function AvulsoLoteModal({ ctx, onClose, onSaved }) {
  const linhas = ctx.linhas || [];
  const [form, setForm] = useState({
    categoria: '', subcategoria: '', xm_categoria_id: null, empresa: '', conciliar: true,
  });
  const [saving, setSaving] = useState(false);
  const total = linhas.reduce((s, t) => s + (parseFloat(t.valor) || 0), 0);

  const inp = {
    width: '100%', padding: '8px 10px', borderRadius: 8, border: `1px solid ${colors.border}`,
    background: colors.bg, color: colors.text, fontSize: 14, boxSizing: 'border-box',
  };
  const sel = { ...inp, appearance: 'none' };
  const lbl = { fontSize: 12, color: colors.textMuted, display: 'block', marginBottom: 4 };

  const salvar = async () => {
    if (saving || !linhas.length) return;
    setSaving(true);
    try {
      const r = await fetch(`${API}/conciliacao/avulso-lote`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          transacao_ids: linhas.map(t => t.id),
          categoria: form.categoria || null, subcategoria: form.subcategoria || null,
          xm_categoria_id: form.xm_categoria_id ?? null,
          empresa: form.empresa || null, conciliar: form.conciliar,
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      onSaved(d);
    } catch (e) { alert('Avulso em lote: ' + e.message); }
    setSaving(false);
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.6)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: colors.card, borderRadius: 16, padding: 28, width: 520,
        border: `1px solid ${colors.border}`, boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
        maxHeight: '90vh', overflowY: 'auto',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: colors.text }}>Avulso em lote</div>
            <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 2 }}>
              {linhas.length} linha(s) · {fmt(total)}
              {ctx.ignoradas > 0 && ` · ${ctx.ignoradas} ignorada(s) sem cartão`}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer' }}>
            <X size={18} />
          </button>
        </div>

        <div style={{
          maxHeight: 180, overflowY: 'auto', border: `1px solid ${colors.border}`,
          borderRadius: 8, marginBottom: 14,
        }}>
          {linhas.map(t => (
            <div key={t.id} style={{
              display: 'flex', justifyContent: 'space-between', gap: 8, padding: '6px 10px',
              borderBottom: `1px solid ${colors.border}22`, fontSize: 12, color: colors.text,
            }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {t.descricao}{t.parcela ? ` [${t.parcela}]` : ''}
              </span>
              <span style={{ whiteSpace: 'nowrap', color: colors.warning, fontWeight: 600 }}>{fmt(t.valor)}</span>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <CamposCategoria categoria={form.categoria} subcategoria={form.subcategoria} empresa={form.empresa}
            onChange={(p) => setForm(f => ({ ...f, ...p }))} inp={inp} sel={sel} lbl={lbl} />

          <div>
            <label style={lbl}><Building2 size={11} style={{ verticalAlign: -1 }} /> Empresa (opcional)</label>
            <select style={sel} value={form.empresa} onChange={e => setForm(f => ({ ...f, empresa: e.target.value }))}>
              <option value="">— sem empresa</option>
              {EMPRESAS.map(e => <option key={e} value={e}>{e}</option>)}
            </select>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: colors.text, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.conciliar}
              onChange={e => setForm(f => ({ ...f, conciliar: e.target.checked }))} />
            Conciliar cada avulso com a própria linha da fatura (recomendado)
          </label>

          <div style={{ fontSize: 11, color: colors.textMuted, background: `${colors.info}10`,
            border: `1px solid ${colors.info}30`, borderRadius: 8, padding: '8px 10px' }}>
            Cada linha vira um avulso <strong>espelho</strong> (mesma descrição, valor, data e
            parcela) com a classificação acima. {form.conciliar
              ? 'Os pares já saem conciliados — desfazer é pelo botão normal, linha a linha.'
              : 'Os avulsos entram pendentes na lista do XMiles pra conciliar depois.'}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 22, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{
            padding: '8px 20px', borderRadius: 8, border: `1px solid ${colors.border}`,
            background: 'transparent', color: colors.textMuted, cursor: 'pointer', fontSize: 14,
          }}>Cancelar</button>
          <button onClick={salvar} disabled={saving || !linhas.length}
            style={{
              padding: '8px 20px', borderRadius: 8, border: 'none',
              background: saving ? colors.border : colors.primary, color: '#fff',
              cursor: saving ? 'default' : 'pointer', fontSize: 14, fontWeight: 600,
            }}>
            {saving ? 'Lançando...' : `Lançar ${linhas.length}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function LancamentoModal({ ctx, onClose, onSaved }) {
  const agora = new Date();
  const diaHoje = Math.min(agora.getDate(), 28);
  // Chamado #44: data completa (dia/mês/ano) no avulso — default hoje (fuso local).
  const hojeISO = `${agora.getFullYear()}-${String(agora.getMonth() + 1).padStart(2, '0')}-${String(agora.getDate()).padStart(2, '0')}`;
  const [form, setForm] = useState({
    // chamado #42: valor/descrição já vêm preenchidos quando o avulso é aberto a
    // partir de um lançamento da fatura (botão Avulso na linha).
    tipo: 'avulso', descricao: ctx.descricao || '', valor: ctx.valor != null && ctx.valor !== '' ? String(ctx.valor) : '',
    data_lancamento: hojeISO, dia_lancamento: String(diaHoje), total_parcelas: '12', empresa: '',
    categoria: '', subcategoria: '', xm_categoria_id: null,
  });
  const [saving, setSaving] = useState(false);
  const recorrente = form.tipo === 'recorrente';
  // Chamado #53: valor negativo é válido (crédito/estorno) — só zero/vazio não.
  const valido = form.descricao.trim() && !!parseFloat(form.valor) &&
    (recorrente ? parseInt(form.total_parcelas) >= 1 : /^\d{4}-\d{2}-\d{2}$/.test(form.data_lancamento));

  const inp = {
    width: '100%', padding: '8px 10px', borderRadius: 8, border: `1px solid ${colors.border}`,
    background: colors.bg, color: colors.text, fontSize: 14, boxSizing: 'border-box',
  };
  const sel = { ...inp, appearance: 'none' };
  const lbl = { fontSize: 12, color: colors.textMuted, display: 'block', marginBottom: 4 };

  const salvar = async () => {
    if (!valido || saving) return;
    setSaving(true);
    try {
      const body = {
        cartao_id: parseInt(ctx.cartao_id),
        mes_referencia: ctx.mes_referencia,
        descricao: form.descricao.trim(),
        valor: parseFloat(form.valor),
        tipo: form.tipo,
        empresa: form.empresa || null,
        categoria: form.categoria.trim() || null,
        subcategoria: form.subcategoria.trim() || null,
        // Vínculo com o plano de contas do TechMiles (null = categoria local).
        // Com ele, o lançamento já nasce classificado — sem depender de casar
        // por nome depois em /api/plano-contas/migrar.
        xm_categoria_id: form.xm_categoria_id ?? null,
      };
      // Chamado #44: avulso manda a data completa; recorrente manda o dia do mês.
      if (recorrente) {
        body.dia_lancamento = parseInt(form.dia_lancamento) || 1;
        body.total_parcelas = parseInt(form.total_parcelas) || 1;
      } else {
        body.data_lancamento = form.data_lancamento;
      }
      const r = await fetch(`${API}/conciliacao/lancamento`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      onSaved();
    } catch (e) { alert('Erro ao lançar: ' + e.message); setSaving(false); }
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.6)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: colors.card, borderRadius: 16, padding: 28, width: 480,
        border: `1px solid ${colors.border}`, boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
        maxHeight: '90vh', overflowY: 'auto',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: colors.text }}>Fazer Lançamento</div>
            <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 2 }}>
              {ctx.nome_cartao} · {ctx.mes_referencia}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer' }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Tipo: avulso ou recorrente */}
          <div>
            <label style={lbl}>Tipo</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {[['avulso', 'Avulso', PlusCircle], ['recorrente', 'Recorrente', Repeat]].map(([val, label, Icon]) => (
                <button key={val} onClick={() => setForm(f => ({ ...f, tipo: val }))}
                  style={{
                    flex: 1, padding: '8px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    border: `2px solid ${form.tipo === val ? colors.primary : colors.border}`,
                    background: form.tipo === val ? `${colors.primary}22` : 'transparent',
                    color: form.tipo === val ? colors.primary : colors.textMuted,
                  }}>
                  <Icon size={14} /> {label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label style={lbl}>Descrição</label>
            <input style={inp} value={form.descricao} placeholder="Ex: Mensalidade, Seguro, Anuidade..."
              onChange={e => setForm(f => ({ ...f, descricao: e.target.value }))} />
          </div>

          {/* Categoria (grupo) / subcategoria (despesa) — tipos do TechMiles/
              Manager (chamado #42) + criação de categoria local (03/09/2026) */}
          <CamposCategoria categoria={form.categoria} subcategoria={form.subcategoria} empresa={form.empresa}
            onChange={(p) => setForm(f => ({ ...f, ...p }))} inp={inp} sel={sel} lbl={lbl} />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={lbl}>Valor (R$){recorrente ? ' por parcela' : ''}</label>
              <input style={inp} type="number" step="0.01" value={form.valor}
                onChange={e => setForm(f => ({ ...f, valor: e.target.value }))} />
            </div>
            <div>
              {/* Chamado #44: avulso pede a data completa (dia/mês/ano); recorrente
                  continua com o dia do mês, que se repete a cada parcela. */}
              {recorrente ? (<>
                <label style={lbl}>Dia do lançamento (1-28)</label>
                <input style={inp} type="number" min="1" max="28" value={form.dia_lancamento}
                  onChange={e => setForm(f => ({ ...f, dia_lancamento: e.target.value }))} />
              </>) : (<>
                <label style={lbl}>Data do lançamento</label>
                <input style={inp} type="date" value={form.data_lancamento}
                  onChange={e => setForm(f => ({ ...f, data_lancamento: e.target.value }))} />
              </>)}
            </div>
          </div>

          {recorrente && (
            <div>
              <label style={lbl}>Quantidade de parcelas</label>
              <input style={{ ...inp, width: '50%' }} type="number" min="1" value={form.total_parcelas}
                onChange={e => setForm(f => ({ ...f, total_parcelas: e.target.value }))} />
              <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 6 }}>
                A 1ª parcela entra em {ctx.mes_referencia}; as demais aparecem sozinhas a cada mês até completar {parseInt(form.total_parcelas) || 1}.
              </div>
            </div>
          )}

          <div>
            <label style={lbl}><Building2 size={11} style={{ verticalAlign: -1 }} /> Empresa (opcional)</label>
            <select style={sel} value={form.empresa} onChange={e => setForm(f => ({ ...f, empresa: e.target.value }))}>
              <option value="">— sem empresa (definir no match)</option>
              {EMPRESAS.map(e => <option key={e} value={e}>{e}</option>)}
            </select>
          </div>

          <div style={{ fontSize: 11, color: colors.textMuted, background: `${colors.info}10`,
            border: `1px solid ${colors.info}30`, borderRadius: 8, padding: '8px 10px' }}>
            O lançamento entra na <strong>lista do XMiles</strong> como pendente. Depois é só
            dar <strong>Match</strong> com a linha da fatura pra conciliar (a empresa pode ser
            escolhida nesse momento).
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 22, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{
            padding: '8px 20px', borderRadius: 8, border: `1px solid ${colors.border}`,
            background: 'transparent', color: colors.textMuted, cursor: 'pointer', fontSize: 14,
          }}>Cancelar</button>
          <button
            onClick={salvar}
            disabled={!valido || saving}
            style={{
              padding: '8px 20px', borderRadius: 8, border: 'none',
              background: colors.primary, color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600,
              opacity: (!valido || saving) ? 0.5 : 1,
            }}>
            {saving ? 'Lançando...' : recorrente ? 'Criar Recorrência' : 'Lançar'}
          </button>
        </div>
      </div>
    </div>
  );
}
